import Phaser from "phaser";
import "./styles.css";
import {
  ABILITIES,
  ABILITY_COLORS,
  ABILITY_NAMES,
  AMBER_CACHE_X,
  AMBER_CACHE_Y,
  ARENA_HEIGHT,
  ARENA_WIDTH,
  BAIT_PEN_X,
  BAIT_PEN_Y,
  OBJECTIVE_STATION_RADIUS,
  PARK_LANDMARKS,
  PERSONAL_CHALLENGE_COMPLETE_DISPLAY_MS,
  REVIVE_RADIUS,
  REVIVE_SHIELD_DURATION_MS,
  SURVIVOR_SKINS,
  ULTIMATE_COOLDOWNS,
  ULTIMATE_NAMES,
  VIEWPORT_HEIGHT,
  VIEWPORT_WIDTH,
  normalizeSurvivorSkin,
  type AbilityId,
  type SurvivorSkin,
} from "../shared/constants";
import type {
  CombatFxTuple,
  EnemyTuple,
  LootId,
  LootMessage,
  LootOption,
  MatchStage,
  PlayerStateMessage,
  PlayerTuple,
  PickupMessage,
  PowerUpTuple,
  RaidWarningMessage,
  ServerMessage,
  SnapshotMessage,
  SummonTuple,
} from "../shared/protocol";
import {
  canvasPointToArena,
  specialTargetPoint,
  touchDragDirection,
  viewportPointToWorld,
} from "../shared/touch";
import {
  consumeUltimateBarrageCharge,
  temporaryMoveSpeedMultiplier,
} from "../shared/rules";
import { survivorMotionFrame } from "../shared/animation";

interface JoinConfig {
  name: string;
  ability: AbilityId;
  session: string;
}

interface PlayerView {
  body: Phaser.GameObjects.Arc;
  sprite: Phaser.GameObjects.Sprite;
  skin: SurvivorSkin;
  outline: Phaser.GameObjects.Arc;
  name: Phaser.GameObjects.Text;
  evolution: Phaser.GameObjects.Text;
  level: Phaser.GameObjects.Text;
  rune: Phaser.GameObjects.Text;
  revive: Phaser.GameObjects.Text;
  targetX: number;
  targetY: number;
  ability: AbilityId;
  ghost: boolean;
  reviveProgress: number;
  reviveShieldUntil: number;
  inspiredUntil: number;
  range: number;
}

interface DeathBurst {
  x: number;
  y: number;
  color: number;
  createdAt: number;
  kind?: EnemyTuple[1];
}

interface AnimatedLandmark {
  image: Phaser.GameObjects.Image;
  kind: "research" | "incubator" | "warning";
  baseRotation: number;
  phase: number;
}

interface EnemyView {
  body:
    | Phaser.GameObjects.Arc
    | Phaser.GameObjects.Star
    | Phaser.GameObjects.Rectangle;
  sprite: Phaser.GameObjects.Sprite | null;
  dinoAnims: { left: string; right: string } | null;
  baseAlpha: number;
  healthBg: Phaser.GameObjects.Rectangle;
  health: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text | null;
  rune: Phaser.GameObjects.Text | null;
  targetX: number;
  targetY: number;
  kind: EnemyTuple[1];
  hpRatio: number;
  size: number;
}

interface PowerUpView {
  orb: Phaser.GameObjects.Arc;
  icon: Phaser.GameObjects.Text | Phaser.GameObjects.Image;
  kind: PowerUpTuple[1];
  expiresAt: number;
}

interface SummonView {
  body: Phaser.GameObjects.Arc;
  sprite: Phaser.GameObjects.Sprite;
  outline: Phaser.GameObjects.Arc;
  rune: Phaser.GameObjects.Text;
  targetX: number;
  targetY: number;
  kind: SummonTuple[1];
  size: number;
  expiresAt: number;
}

interface ArenaTouchState {
  pointerId: number | null;
  startClientX: number;
  startClientY: number;
  x: number;
  y: number;
  targetX: number | null;
  targetY: number | null;
}

const dom = {
  join: required<HTMLElement>("join-screen"),
  name: required<HTMLInputElement>("name-input"),
  starterGrid: required<HTMLElement>("starter-grid"),
  selectedGearLabel: required<HTMLElement>("selected-gear-label"),
  deployButton: required<HTMLButtonElement>("deploy-button"),
  joinError: required<HTMLElement>("join-error"),
  hud: required<HTMLElement>("hud"),
  playerHud: required<HTMLElement>("player-hud"),
  stage: required<HTMLElement>("stage-label"),
  round: required<HTMLElement>("round-label"),
  timer: required<HTMLElement>("timer-label"),
  playerCount: required<HTMLElement>("player-count"),
  connection: required<HTMLElement>("connection-pill"),
  healthMeter: required<HTMLElement>("health-meter"),
  healthLabel: required<HTMLElement>("health-label"),
  xpMeter: required<HTMLElement>("xp-meter"),
  xpLabel: required<HTMLElement>("xp-label"),
  levelLabel: required<HTMLElement>("level-label"),
  abilityLabel: required<HTMLButtonElement>("ability-label"),
  bossHud: required<HTMLElement>("boss-hud"),
  bossMeter: required<HTMLElement>("boss-meter"),
  bossPhase: required<HTMLElement>("boss-phase"),
  personalChallenge: required<HTMLElement>("personal-challenge"),
  challengeName: required<HTMLElement>("challenge-name"),
  challengeCopy: required<HTMLElement>("challenge-copy"),
  challengeStatus: required<HTMLElement>("challenge-status"),
  challengeMeter: required<HTMLElement>("challenge-meter"),
  raidUltimate: required<HTMLElement>("raid-ultimate"),
  raidUltimateLabel: required<HTMLElement>("raid-ultimate-label"),
  raidUltimateMeter: required<HTMLElement>("raid-ultimate-meter"),
  raidUltimateButton: required<HTMLButtonElement>("raid-ultimate-button"),
  evolutionChip: required<HTMLElement>("evolution-chip"),
  teamBuffs: required<HTMLElement>("team-buffs"),
  teamBuffDamage: required<HTMLElement>("team-buff-damage"),
  teamBuffHaste: required<HTMLElement>("team-buff-haste"),
  teamBuffVitality: required<HTMLElement>("team-buff-vitality"),
  teamBuffSpeed: required<HTMLElement>("team-buff-speed"),
  teamBuffArtifacts: required<HTMLElement>("team-buff-artifacts"),
  teamBuffTruesight: required<HTMLElement>("team-buff-truesight"),
  raidWarning: required<HTMLElement>("raid-warning"),
  raidWarningKicker: required<HTMLElement>("raid-warning-kicker"),
  raidWarningTitle: required<HTMLElement>("raid-warning-title"),
  raidWarningCopy: required<HTMLElement>("raid-warning-copy"),
  lootPanel: required<HTMLElement>("loot-panel"),
  lootSource: required<HTMLElement>("loot-source"),
  lootCards: required<HTMLElement>("loot-cards"),
  lootProtection: required<HTMLElement>("loot-protection"),
  lootTimer: required<HTMLElement>("loot-timer"),
  classPanel: required<HTMLElement>("class-panel"),
  classCards: required<HTMLElement>("class-cards"),
  classSummary: required<HTMLElement>("class-summary"),
  ghostPanel: required<HTMLElement>("ghost-panel"),
  ghostCause: required<HTMLElement>("ghost-cause"),
  respawnTimer: required<HTMLElement>("respawn-timer"),
  results: required<HTMLElement>("results-screen"),
  resultTitle: required<HTMLElement>("result-title"),
  resultCopy: required<HTMLElement>("result-copy"),
  specialChip: required<HTMLElement>("special-chip"),
  specialName: required<HTMLElement>("special-name"),
  specialCooldown: required<HTMLElement>("special-cooldown"),
  relicCount: required<HTMLElement>("relic-count"),
  moteCount: required<HTMLElement>("mote-count"),
  moteBank: required<HTMLElement>("mote-bank"),
  baitCount: required<HTMLElement>("bait-count"),
  baitBank: required<HTMLElement>("bait-bank"),
  overdriveChip: required<HTMLElement>("overdrive-chip"),
  teamSurgeChip: required<HTMLElement>("team-surge-chip"),
  teamComboChip: required<HTMLElement>("team-combo-chip"),
  powerupChip: required<HTMLElement>("powerup-chip"),
  fogOverlay: required<HTMLElement>("fog-overlay"),
  resultRound: required<HTMLElement>("result-round"),
  leaderboardRows: required<HTMLElement>("leaderboard-rows"),
  roundMedals: required<HTMLElement>("round-medals"),
  artifactVotePanel: required<HTMLElement>("artifact-vote-panel"),
  artifactVoteCards: required<HTMLElement>("artifact-vote-cards"),
  resultsCountdown: required<HTMLElement>("results-countdown"),
  readyCount: required<HTMLElement>("ready-count"),
  readyButton: required<HTMLButtonElement>("ready-button"),
  toastStack: required<HTMLElement>("toast-stack"),
  mobileControls: required<HTMLElement>("mobile-controls"),
  touchStick: required<HTMLElement>("touch-stick"),
  touchKnob: required<HTMLElement>("touch-knob"),
  touchBomb: required<HTMLButtonElement>("touch-bomb"),
  touchSpecialName: required<HTMLElement>("touch-special-name"),
  touchSpecialCooldown: required<HTMLElement>("touch-special-cooldown"),
  touchDash: required<HTMLButtonElement>("touch-dash"),
  touchDashCooldown: required<HTMLElement>("touch-dash-cooldown"),
  versionBanner: required<HTMLElement>("version-banner"),
  versionRefresh: required<HTMLButtonElement>("version-refresh"),
  versionStamp: required<HTMLElement>("version-stamp"),
  arenaMinimap: required<HTMLCanvasElement>("arena-minimap"),
  arenaBoundary: required<HTMLElement>("arena-boundary"),
  arenaBoundaryLabel: required<HTMLElement>("arena-boundary-label"),
  arenaBoundaryDistance: required<HTMLElement>("arena-boundary-distance"),
};

dom.versionStamp.textContent = "v" + __APP_SEMVER__;
dom.versionStamp.title = "HOTYB v" + __APP_SEMVER__ + " · build " + __APP_VERSION__;

const savedName = localStorage.getItem("hotyb-name");
if (savedName) dom.name.value = savedName;
dom.name.addEventListener("keydown", (event) => event.stopPropagation());
dom.name.addEventListener("keyup", (event) => event.stopPropagation());

class GameConnection extends EventTarget {
  socket: WebSocket | null = null;
  localId = "";
  session = "";
  serverOffset = 0;
  private desired = false;
  private config: JoinConfig | null = null;
  private reconnectAttempt = 0;

  connect(config: JoinConfig): void {
    this.config = config;
    this.session = config.session;
    this.desired = true;
    this.open();
  }

  disconnect(): void {
    this.desired = false;
    this.config = null;
    this.reconnectAttempt = 0;
    this.localId = "";
    const socket = this.socket;
    this.socket = null;
    if (
      socket &&
      (socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING)
    ) {
      socket.close(1000, "returned to main screen");
    }
    this.setConnectionState("offline");
  }

  send(message: object): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  rememberAbility(ability: AbilityId): void {
    if (this.config) this.config.ability = ability;
  }

  private open(): void {
    if (!this.config) return;
    this.setConnectionState("connecting");
    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    const params = new URLSearchParams({
      name: this.config.name,
      ability: this.config.ability,
      session: this.session,
      version: __APP_VERSION__,
      mxp: String(metaXpLevel()),
      mregen: String(metaRegenLevel()),
      skin: selectedSkin(),
    });
    if (location.hostname === "127.0.0.1" || location.hostname === "localhost") {
      const localParams = new URLSearchParams(location.search);
      if (localParams.get("mode") === "fast") params.set("mode", "fast");
      const demoSpawn = localParams.get("spawn");
      if (demoSpawn) params.set("spawn", demoSpawn);
      const demoSupply = localParams.get("supply");
      if (demoSupply) params.set("supply", demoSupply);
    }
    const socket = new WebSocket(
      protocol + "//" + location.host + "/ws?" + params.toString(),
    );
    this.socket = socket;

    socket.addEventListener("open", () => {
      this.reconnectAttempt = 0;
      this.setConnectionState("online");
    });

    socket.addEventListener("message", (event) => {
      if (typeof event.data !== "string") return;
      let message: ServerMessage | { t: "pong"; at: number };
      try {
        message = JSON.parse(event.data) as ServerMessage | { t: "pong"; at: number };
      } catch {
        return;
      }
      if (message.t === "pong") return;
      if (message.t === "welcome") {
        this.localId = message.id;
        this.session = message.session;
        localStorage.setItem("hotyb-session", message.session);
      }
      if (message.t === "snapshot") {
        this.serverOffset = message.now - Date.now();
      }
      this.dispatchEvent(new CustomEvent("server", { detail: message }));
    });

    socket.addEventListener("close", () => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.setConnectionState("offline");
      if (!this.desired) return;
      const delay = Math.min(5_000, 400 * 2 ** this.reconnectAttempt);
      this.reconnectAttempt += 1;
      window.setTimeout(() => this.open(), delay);
    });

    socket.addEventListener("error", () => {
      if (!this.localId) {
        dom.joinError.textContent =
          "The vault could not be reached. It may be full—trying again…";
      }
    });
  }

  private setConnectionState(state: "connecting" | "online" | "offline"): void {
    dom.connection.textContent = state;
    dom.connection.dataset.state = state;
  }
}

const connection = new GameConnection();
const mobileMovement = { x: 0, y: 0 };
const arenaTouch: ArenaTouchState = {
  pointerId: null,
  startClientX: 0,
  startClientY: 0,
  x: 0,
  y: 0,
  targetX: null,
  targetY: null,
};
const coarsePointer = window.matchMedia(
  "(hover: none), (pointer: coarse), (any-pointer: coarse)",
).matches;
const touchPreview = new URLSearchParams(window.location.search).get("touch") === "1";
const touchInput = coarsePointer || touchPreview;
document.body.classList.toggle("touch-input", touchInput);
const telemetryVisit = crypto.randomUUID();
const lobbyOpenedAt = performance.now();
let telemetryRunStartedAt = 0;
let telemetryFirstLevelSent = false;
let telemetryRunEndKey = "";

type TelemetryData = Record<string, string | number | boolean>;

function telemetryAllowed(): boolean {
  const privacyNavigator = navigator as Navigator & {
    globalPrivacyControl?: boolean;
  };
  return !privacyNavigator.globalPrivacyControl && navigator.doNotTrack !== "1";
}

function telemetryContext(): TelemetryData {
  return {
    device: touchInput ? "touch" : "pointer",
    orientation: innerHeight > innerWidth ? "portrait" : "landscape",
    viewportWidth: innerWidth,
    viewportHeight: innerHeight,
  };
}

function sendTelemetry(
  event: "lobby_view" | "gear_selected" | "run_start" | "first_level" | "run_end" | "session_exit",
  data: TelemetryData = {},
  unloading = false,
): void {
  if (!telemetryAllowed()) return;
  const body = JSON.stringify({
    event,
    visit: telemetryVisit,
    data: { ...telemetryContext(), ...data },
  });
  if (unloading && navigator.sendBeacon) {
    navigator.sendBeacon("/api/telemetry", body);
    return;
  }
  void fetch("/api/telemetry", {
    method: "POST",
    body,
    keepalive: true,
  }).catch(() => {
    // Telemetry must never interrupt play.
  });
}

function trackRunStart(ability: AbilityId): void {
  telemetryRunStartedAt = performance.now();
  telemetryFirstLevelSent = false;
  telemetryRunEndKey = "";
  sendTelemetry("run_start", {
    ability,
    skin: selectedSkin(),
    msToDeploy: Math.round(telemetryRunStartedAt - lobbyOpenedAt),
  });
}

sendTelemetry("lobby_view");
window.addEventListener("pagehide", () => {
  sendTelemetry(
    "session_exit",
    {
      state: document.body.classList.contains("results-open")
        ? "results"
        : document.body.classList.contains("game-active")
          ? "run"
          : "lobby",
      runMs: telemetryRunStartedAt
        ? Math.round(performance.now() - telemetryRunStartedAt)
        : 0,
      level: playerState?.level ?? 0,
    },
    true,
  );
});

class VaultScene extends Phaser.Scene {
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasd!: Record<"up" | "down" | "left" | "right", Phaser.Input.Keyboard.Key>;
  private players = new Map<string, PlayerView>();
  private enemies = new Map<number, EnemyView>();
  private summons = new Map<number, SummonView>();
  private powerUps = new Map<number, PowerUpView>();
  private snapshot: SnapshotMessage | null = null;
  private combatFx = new Map<number, CombatFxTuple>();
  private deathBursts: DeathBurst[] = [];
  private hazardLayer!: Phaser.GameObjects.Graphics;
  private amberCacheSprite!: Phaser.GameObjects.Image;
  private baitPenSprite!: Phaser.GameObjects.Image;
  private goatSprite!: Phaser.GameObjects.Image;
  private rallyFlareSprite!: Phaser.GameObjects.Image;
  private rallySmokeSprite!: Phaser.GameObjects.Sprite;
  private landmarkSparkSprite!: Phaser.GameObjects.Sprite;
  private animatedLandmarks: AnimatedLandmark[] = [];
  private pickupCallout: Phaser.GameObjects.Text | null = null;
  private pendingPickupCallout: PickupMessage | null = null;
  private cableSprites: Phaser.GameObjects.Image[] = [];
  private sparkSprites: Phaser.GameObjects.Sprite[] = [];
  private bogSprites: Phaser.GameObjects.Image[] = [];
  private stompCrackSprites: Phaser.GameObjects.Image[] = [];
  private dustBurstSprites: Phaser.GameObjects.Image[] = [];
  private hatchSprites: Phaser.GameObjects.Sprite[] = [];
  private venomProjectileSprites: Phaser.GameObjects.Image[] = [];
  private venomSplatterSprites: Phaser.GameObjects.Image[] = [];
  private tranqRuptureSprites: Phaser.GameObjects.Sprite[] = [];
  private stampedeSprites: Phaser.GameObjects.Sprite[] = [];
  private amberSprites: Phaser.GameObjects.Image[] = [];
  private effectLayer!: Phaser.GameObjects.Graphics;
  private arenaBoundaryLayer!: Phaser.GameObjects.Graphics;
  private activeArena: [number, number, number, number] = [
    0,
    0,
    ARENA_WIDTH,
    ARENA_HEIGHT,
  ];
  private lastInputX = 99;
  private lastInputY = 99;
  private lastInputAt = 0;
  private inputSeq = 0;
  private inputX = 0;
  private inputY = 0;
  private localDashUntil = 0;
  private localDashReadyAt = 0;
  private localDashX = 0;
  private localDashY = 0;
  private followedPlayerId = "";

  constructor() {
    super("vault");
  }

  preload(): void {
    this.load.spritesheet("jham", "/assets/hotyb/jham_redesign_sheet.png", { frameWidth: 64, frameHeight: 72 });
    this.load.spritesheet("neddy", "/assets/hotyb/neddy_redesign_sheet.png", { frameWidth: 64, frameHeight: 72 });
    this.load.spritesheet("orem", "/assets/hotyb/orem_redesign_sheet.png", { frameWidth: 64, frameHeight: 72 });
    this.load.spritesheet("docsalad", "/assets/hotyb/doc_salad_sheet.png", { frameWidth: 64, frameHeight: 72 });
    this.load.spritesheet("chaosmal", "/assets/hotyb/chaos_mal_sheet.png", { frameWidth: 64, frameHeight: 72 });
    this.load.spritesheet("wuops", "/assets/hotyb/wuops_sheet.png", { frameWidth: 64, frameHeight: 72 });
    this.load.spritesheet("clairebear", "/assets/hotyb/claire_bear_sheet.png", { frameWidth: 64, frameHeight: 72 });
    this.load.spritesheet("lockbox", "/assets/hotyb/lockbox_sheet.png", { frameWidth: 64, frameHeight: 72 });
    this.load.spritesheet("kwatt", "/assets/hotyb/k_watt_sheet.png", { frameWidth: 64, frameHeight: 72 });
    this.load.spritesheet("slysoya", "/assets/hotyb/sly_soya_sheet.png", { frameWidth: 64, frameHeight: 72 });
    this.load.spritesheet("compy", "/assets/hotyb/compy_redesign_sheet.png", { frameWidth: 48, frameHeight: 40 });
    this.load.spritesheet("dilo", "/assets/hotyb/dilo_redesign_sheet.png", { frameWidth: 80, frameHeight: 56 });
    this.load.spritesheet("carno", "/assets/hotyb/carno_redesign_sheet.png", { frameWidth: 112, frameHeight: 72 });
    this.load.spritesheet("galli", "/assets/hotyb/galli_redesign_sheet.png", { frameWidth: 80, frameHeight: 56 });
    this.load.spritesheet("ptera", "/assets/hotyb/pteranodon.png", { frameWidth: 32, frameHeight: 32 });
    this.load.spritesheet("raptor", "/assets/hotyb/raptor_redesign_sheet.png", { frameWidth: 80, frameHeight: 56 });
    this.load.spritesheet("irex", "/assets/hotyb/irex.png", { frameWidth: 64, frameHeight: 64 });
    this.load.image("amber", "/assets/hotyb/refined_amber.png");
    this.load.image("pu-fury", "/assets/hotyb/item_not_a_flamethrower.png");
    this.load.image("pu-haste", "/assets/hotyb/item_raptor_energy.png");
    this.load.image("pu-shield", "/assets/hotyb/item_body_armor.png");
    this.load.image("pu-nova", "/assets/hotyb/item_fossil_fuel.png");
    this.load.image("pu-jello", "/assets/hotyb/item_jello.png");
    this.load.image("pu-egg", "/assets/hotyb/item_egg.png");
    this.load.image("pu-bait", "/assets/hotyb/item_meat_bait.png");
    this.load.image("pu-truesight", "/assets/hotyb/item_emergency_radio.png");
    this.load.image("pu-barrage", "/assets/hotyb/item_plushy_launcher.png");
    this.load.image("pu-rod", "/assets/hotyb/item_dna_vial.png");
    this.load.image("tranq-canister", "/assets/hotyb/tranq_canister.png");
    this.load.spritesheet("spino", "/assets/hotyb/spino_redesign_sheet.png", { frameWidth: 128, frameHeight: 80 });
    this.load.spritesheet("brachio", "/assets/hotyb/brachio_redesign_sheet.png", { frameWidth: 112, frameHeight: 112 });
    this.load.spritesheet("otiles", "/assets/hotyb/original_tiles.png", { frameWidth: 16, frameHeight: 16 });
    this.load.image("tree-a", "/assets/hotyb/tree_a.png");
    this.load.image("tree-b", "/assets/hotyb/tree_b.png");
    this.load.image("fern-bush", "/assets/hotyb/fern_bush.png");
    this.load.image("rock-big", "/assets/hotyb/rock_big.png");
    this.load.spritesheet("ankylo", "/assets/hotyb/ankylo_redesign_sheet.png", { frameWidth: 64, frameHeight: 40 });
    this.load.spritesheet("trex", "/assets/hotyb/trex_redesign_sheet.png", { frameWidth: 96, frameHeight: 96 });
    this.load.image("goat", "/assets/hotyb/goat.png");
    this.load.image("objective-amber-cache", "/assets/hotyb/objective_amber_cache.png");
    this.load.image("objective-bait-pen", "/assets/hotyb/objective_bait_pen.png");
    this.load.image("enemy-bog-carno", "/assets/hotyb/enemy_bog_carno.png");
    this.load.image("enemy-bog-spitter", "/assets/hotyb/enemy_bog_spitter.png");
    this.load.image("enemy-fence-jumper", "/assets/hotyb/enemy_fence_jumper.png");
    this.load.image("enemy-nest-matriarch", "/assets/hotyb/enemy_nest_matriarch.png");
    this.load.image("enemy-siren-ptera", "/assets/hotyb/enemy_siren_ptera.png");
    this.load.image("rally-flare", "/assets/hotyb/rally_flare.png");
    this.load.spritesheet("effect-rally-smoke-sequence", "/assets/hotyb/effect_rally_smoke_sequence.png", { frameWidth: 32, frameHeight: 64 });
    this.load.image("effect-broken-cable", "/assets/hotyb/effect_broken_cable.png");
    this.load.spritesheet("effect-electric-spark-sequence", "/assets/hotyb/effect_electric_spark_sequence.png", { frameWidth: 32, frameHeight: 32 });
    this.load.image("effect-bog-puddle", "/assets/hotyb/effect_bog_puddle.png");
    this.load.image("effect-venom-projectile", "/assets/hotyb/effect_venom_projectile.png");
    this.load.image("effect-venom-splatter", "/assets/hotyb/effect_venom_splatter.png");
    this.load.image("effect-stomp-cracks", "/assets/hotyb/effect_stomp_cracks.png");
    this.load.image("effect-dust-burst", "/assets/hotyb/effect_dust_burst.png");
    this.load.spritesheet("effect-egg-sequence", "/assets/hotyb/effect_egg_sequence.png", { frameWidth: 32, frameHeight: 32 });
    this.load.spritesheet("effect-tranq-rupture-sequence", "/assets/hotyb/effect_tranq_rupture_sequence.png", { frameWidth: 48, frameHeight: 48 });
    this.load.image("prop-wrecked-tour-vehicle", "/assets/hotyb/prop_wrecked_tour_vehicle.png");
    this.load.image("prop-broken-paddock-gate", "/assets/hotyb/prop_broken_paddock_gate.png");
    this.load.image("prop-ranger-crates", "/assets/hotyb/prop_ranger_crates.png");
    this.load.image("prop-hatchery-equipment", "/assets/hotyb/prop_hatchery_equipment.png");
    this.load.image("prop-abandoned-incubator", "/assets/hotyb/prop_abandoned_incubator.png");
    this.load.image("prop-warning-sign", "/assets/hotyb/prop_warning_sign.png");
    this.load.image("decal-tire-tracks", "/assets/hotyb/decal_tire_tracks.png");
    this.load.image("decal-giant-footprint", "/assets/hotyb/decal_giant_footprint.png");
    this.load.image("prop-dropped-visitor-items", "/assets/hotyb/prop_dropped_visitor_items.png");
    this.load.image("prop-amber-research-station", "/assets/hotyb/prop_amber_research_station.png");
    this.load.image("pu-sea-bass", "/assets/hotyb/food_chilean_sea_bass.png");
    this.load.image("pu-energy-bar", "/assets/hotyb/food_energy_bar.png");
    this.load.image("pu-ice-pop", "/assets/hotyb/food_ice_pop.png");
    this.load.image("pu-canteen", "/assets/hotyb/food_field_canteen.png");
  }

  private createDinoAnims(): void {
    const anims = this.anims;
    for (const skin of SURVIVOR_SKINS) {
      anims.create({ key: skin + "-down", frames: anims.generateFrameNumbers(skin, { start: 0, end: 3 }), frameRate: 10, repeat: -1 });
      anims.create({ key: skin + "-side", frames: anims.generateFrameNumbers(skin, { start: 4, end: 7 }), frameRate: 10, repeat: -1 });
      anims.create({ key: skin + "-up", frames: anims.generateFrameNumbers(skin, { start: 8, end: 11 }), frameRate: 10, repeat: -1 });
      anims.create({ key: skin + "-idle", frames: [{ key: skin, frame: 0 }], frameRate: 1 });
    }
    anims.create({ key: "compy-left", frames: anims.generateFrameNumbers("compy", { start: 0, end: 3 }), frameRate: 12, repeat: -1 });
    anims.create({ key: "compy-right", frames: anims.generateFrameNumbers("compy", { start: 4, end: 7 }), frameRate: 12, repeat: -1 });
    anims.create({ key: "galli-right", frames: anims.generateFrameNumbers("galli", { start: 0, end: 3 }), frameRate: 12, repeat: -1 });
    anims.create({ key: "galli-left", frames: anims.generateFrameNumbers("galli", { start: 4, end: 7 }), frameRate: 12, repeat: -1 });
    anims.create({ key: "dilo-right", frames: anims.generateFrameNumbers("dilo", { start: 0, end: 3 }), frameRate: 10, repeat: -1 });
    anims.create({ key: "dilo-left", frames: anims.generateFrameNumbers("dilo", { start: 4, end: 7 }), frameRate: 10, repeat: -1 });
    anims.create({ key: "dilo-spit-right", frames: anims.generateFrameNumbers("dilo", { start: 8, end: 11 }), frameRate: 10 });
    anims.create({ key: "dilo-spit-left", frames: anims.generateFrameNumbers("dilo", { start: 12, end: 15 }), frameRate: 10 });
    anims.create({ key: "carno-right", frames: anims.generateFrameNumbers("carno", { start: 0, end: 3 }), frameRate: 10, repeat: -1 });
    anims.create({ key: "carno-left", frames: anims.generateFrameNumbers("carno", { start: 4, end: 7 }), frameRate: 10, repeat: -1 });
    anims.create({ key: "ptera-left", frames: anims.generateFrameNumbers("ptera", { start: 0, end: 7 }), frameRate: 9, repeat: -1 });
    anims.create({ key: "ptera-right", frames: anims.generateFrameNumbers("ptera", { start: 8, end: 15 }), frameRate: 9, repeat: -1 });
    anims.create({ key: "raptor-right", frames: anims.generateFrameNumbers("raptor", { start: 0, end: 3 }), frameRate: 12, repeat: -1 });
    anims.create({ key: "raptor-left", frames: anims.generateFrameNumbers("raptor", { start: 4, end: 7 }), frameRate: 12, repeat: -1 });
    anims.create({ key: "ankylo-left", frames: anims.generateFrameNumbers("ankylo", { start: 0, end: 3 }), frameRate: 5, repeat: -1 });
    anims.create({ key: "ankylo-right", frames: anims.generateFrameNumbers("ankylo", { start: 4, end: 7 }), frameRate: 5, repeat: -1 });
    anims.create({ key: "trex-left", frames: anims.generateFrameNumbers("trex", { start: 8, end: 11 }), frameRate: 7, repeat: -1 });
    anims.create({ key: "trex-right", frames: anims.generateFrameNumbers("trex", { start: 12, end: 15 }), frameRate: 7, repeat: -1 });
    anims.create({ key: "trex-bite-right", frames: anims.generateFrameNumbers("trex", { start: 0, end: 3 }), frameRate: 9 });
    anims.create({ key: "trex-bite-left", frames: anims.generateFrameNumbers("trex", { start: 4, end: 7 }), frameRate: 9 });
    anims.create({ key: "spino-right", frames: anims.generateFrameNumbers("spino", { start: 0, end: 3 }), frameRate: 8, repeat: -1 });
    anims.create({ key: "spino-left", frames: anims.generateFrameNumbers("spino", { start: 4, end: 7 }), frameRate: 8, repeat: -1 });
    anims.create({ key: "brachio-right", frames: anims.generateFrameNumbers("brachio", { start: 0, end: 3 }), frameRate: 7, repeat: -1 });
    anims.create({ key: "brachio-left", frames: anims.generateFrameNumbers("brachio", { start: 4, end: 7 }), frameRate: 7, repeat: -1 });
    anims.create({ key: "irex-left", frames: anims.generateFrameNumbers("irex", { start: 38, end: 43 }), frameRate: 8, repeat: -1 });
    anims.create({ key: "irex-right", frames: anims.generateFrameNumbers("irex", { start: 44, end: 49 }), frameRate: 8, repeat: -1 });
  }

  // Ground + flora are deterministic (fixed seed) so every player sees the
  // same park. All of it is visual only — combat space is unchanged.
  private plantJungle(): void {
    const TILE = 32; // 16px art at 2x, matching entity sprite density
    const COLS = Math.ceil(ARENA_WIDTH / TILE);
    const ROWS = Math.ceil(ARENA_HEIGHT / TILE);
    const rng = new Phaser.Math.RandomDataGenerator(["hotyb-park"]);

    // jungle_tiles.png frame map (12 cols): plain/variant grass, dark grass,
    // dirt, tufts, flowers
    const GRASS = 0;
    const GRASS_VAR = [1, 2, 3, 13, 14, 15];
    const DARK = [4, 5];
    const DIRT = [6, 7];
    const TUFTS = [8, 9];
    const FLOWERS = [11, 10, 12];

    const grid: number[][] = [];
    for (let cx = 0; cx < COLS; cx++) {
      grid[cx] = [];
      for (let cy = 0; cy < ROWS; cy++) {
        const roll = rng.frac();
        grid[cx][cy] = roll < 0.88 ? GRASS : GRASS_VAR[rng.between(0, GRASS_VAR.length - 1)];
      }
    }
    // dark undergrowth blobs
    for (let i = 0; i < 14; i++) {
      let bx = rng.between(2, COLS - 3);
      let by = rng.between(2, ROWS - 3);
      for (let step = rng.between(10, 30); step > 0; step--) {
        grid[bx][by] = DARK[rng.between(0, DARK.length - 1)];
        bx = Phaser.Math.Clamp(bx + rng.between(-1, 1), 1, COLS - 2);
        by = Phaser.Math.Clamp(by + rng.between(-1, 1), 1, ROWS - 2);
      }
    }
    // the tour road: a wandering dirt path across the park, plus a spur
    let pathY = Math.floor(ROWS * 0.55);
    for (let cx = 0; cx < COLS; cx++) {
      pathY = Phaser.Math.Clamp(pathY + rng.between(-1, 1), 3, ROWS - 4);
      for (let w = 0; w < 3; w++) {
        grid[cx][pathY + w] = DIRT[rng.between(0, DIRT.length - 1)];
      }
    }
    let spurX = Math.floor(COLS * 0.3);
    for (let cy = 0; cy < Math.floor(ROWS * 0.55); cy++) {
      spurX = Phaser.Math.Clamp(spurX + rng.between(-1, 1), 3, COLS - 4);
      for (let w = 0; w < 2; w++) {
        grid[spurX + w][cy] = DIRT[rng.between(0, DIRT.length - 1)];
      }
    }
    // dirt clearings
    for (let i = 0; i < 8; i++) {
      let bx = rng.between(3, COLS - 4);
      let by = rng.between(3, ROWS - 4);
      for (let step = rng.between(8, 18); step > 0; step--) {
        grid[bx][by] = DIRT[rng.between(0, DIRT.length - 1)];
        bx = Phaser.Math.Clamp(bx + rng.between(-1, 1), 1, COLS - 2);
        by = Phaser.Math.Clamp(by + rng.between(-1, 1), 1, ROWS - 2);
      }
    }

    // bake ground + decals into one texture
    const ground = this.add
      .renderTexture(ARENA_WIDTH / 2, ARENA_HEIGHT / 2, ARENA_WIDTH, ARENA_HEIGHT)
      .setDepth(0.5);
    const stamp = this.make.image({ key: "otiles", add: false }).setOrigin(0, 0).setScale(2);
    ground.beginDraw();
    for (let cx = 0; cx < COLS; cx++) {
      for (let cy = 0; cy < ROWS; cy++) {
        stamp.setFrame(grid[cx][cy]);
        ground.batchDraw(stamp, cx * TILE, cy * TILE);
        const isGrass = grid[cx][cy] < 60 && grid[cx][cy] >= 41;
        if (isGrass) {
          const decalRoll = rng.frac();
          if (decalRoll < 0.045) {
            stamp.setFrame(TUFTS[rng.between(0, TUFTS.length - 1)]);
            ground.batchDraw(stamp, cx * TILE, cy * TILE);
          } else if (decalRoll < 0.062) {
            stamp.setFrame(FLOWERS[rng.between(0, FLOWERS.length - 1)]);
            ground.batchDraw(stamp, cx * TILE, cy * TILE);
          }
        }
      }
    }
    ground.endDraw();
    stamp.destroy();

    // dense treeline framing the whole park; bottom-edge canopies draw over
    // actors for cheap depth
    const treeStep = 30;
    for (let x = -20; x < ARENA_WIDTH + 20; x += treeStep) {
      for (const row of [0, 1]) {
        const jx = x + rng.between(-8, 8);
        this.add
          .image(jx, rng.between(-14, 10) - row * 18, "tree-a")
          .setScale(2)
          .setDepth(3.4);
        this.add
          .image(jx, ARENA_HEIGHT + rng.between(-10, 14) + row * 18, "tree-a")
          .setScale(2)
          .setDepth(7.6);
      }
    }
    for (let y = 20; y < ARENA_HEIGHT - 10; y += treeStep) {
      for (const col of [0, 1]) {
        const jy = y + rng.between(-8, 8);
        this.add
          .image(rng.between(-14, 10) - col * 18, jy, "tree-a")
          .setScale(2)
          .setDepth(3.4);
        this.add
          .image(ARENA_WIDTH + rng.between(-10, 14) + col * 18, jy, "tree-a")
          .setScale(2)
          .setDepth(3.4);
      }
    }

    // interior flora: clustered stands of trees, not confetti
    const clusters: { x: number; y: number }[] = [];
    let attempts = 0;
    while (clusters.length < 14 && attempts < 300) {
      attempts++;
      const cx = rng.between(120, ARENA_WIDTH - 120);
      const cy = rng.between(120, ARENA_HEIGHT - 120);
      const fromCenter = Phaser.Math.Distance.Between(cx, cy, ARENA_WIDTH / 2, ARENA_HEIGHT / 2);
      if (fromCenter < 260) continue;
      if (clusters.some((c) => Phaser.Math.Distance.Between(cx, cy, c.x, c.y) < 300)) continue;
      clusters.push({ x: cx, y: cy });
      const stand = rng.between(2, 4);
      for (let i = 0; i < stand; i++) {
        const key = rng.frac() < 0.55 ? "tree-a" : rng.frac() < 0.5 ? "tree-b" : "fern-bush";
        this.add
          .image(cx + rng.between(-52, 52), cy + rng.between(-36, 36), key)
          .setScale(2)
          .setDepth(3.4);
      }
      if (rng.frac() < 0.4) {
        this.add
          .image(cx + rng.between(-70, 70), cy + rng.between(50, 80), "rock-big")
          .setScale(2)
          .setDepth(0.8);
      }
    }

    // A few readable park landmarks make the map feel inhabited without
    // turning the combat floor into a field of decorative clutter.
    const landmarks: Array<
      [
        key: string,
        x: number,
        y: number,
        depth: number,
        rotation?: number,
        animation?: AnimatedLandmark["kind"],
      ]
    > = [
      ["prop-wrecked-tour-vehicle", PARK_LANDMARKS.tourRoute.x, PARK_LANDMARKS.tourRoute.y, 2.55, -0.08],
      ["prop-warning-sign", PARK_LANDMARKS.tourRoute.x - 120, PARK_LANDMARKS.tourRoute.y - 74, 2.62, -0.05, "warning"],
      ["prop-ranger-crates", PARK_LANDMARKS.rangerCamp.x, PARK_LANDMARKS.rangerCamp.y, 2.5, -0.03],
      ["prop-warning-sign", PARK_LANDMARKS.rangerCamp.x - 76, PARK_LANDMARKS.rangerCamp.y - 48, 2.62, 0.04, "warning"],
      ["prop-broken-paddock-gate", PARK_LANDMARKS.paddockGate.x, PARK_LANDMARKS.paddockGate.y, 2.52, 0.04],
      ["prop-warning-sign", PARK_LANDMARKS.paddockGate.x - 108, PARK_LANDMARKS.paddockGate.y - 58, 2.62, 0.06, "warning"],
      ["prop-dropped-visitor-items", PARK_LANDMARKS.visitorDrop.x, PARK_LANDMARKS.visitorDrop.y, 2.45, 0.08],
      ["prop-hatchery-equipment", PARK_LANDMARKS.hatchery.x - 46, PARK_LANDMARKS.hatchery.y - 8, 2.5, 0.02],
      ["prop-abandoned-incubator", PARK_LANDMARKS.hatchery.x + 62, PARK_LANDMARKS.hatchery.y + 18, 2.52, -0.04, "incubator"],
      ["prop-ranger-crates", PARK_LANDMARKS.hatchery.x - 112, PARK_LANDMARKS.hatchery.y + 42, 2.48, 0.05],
      ["prop-amber-research-station", PARK_LANDMARKS.researchWest.x, PARK_LANDMARKS.researchWest.y, 2.58, -0.02, "research"],
      ["prop-amber-research-station", PARK_LANDMARKS.researchEast.x, PARK_LANDMARKS.researchEast.y, 2.58, 0.03, "research"],
    ];
    landmarks.forEach(
      ([key, x, y, depth, rotation = 0, animation], index) => {
        const image = this.add
          .image(x, y, key)
          .setDepth(depth)
          .setRotation(rotation);
        if (animation) {
          this.animatedLandmarks.push({
            image,
            kind: animation,
            baseRotation: rotation,
            phase: index * 1.37,
          });
        }
      },
    );
    this.landmarkSparkSprite = this.add
      .sprite(
        PARK_LANDMARKS.paddockGate.x - 8,
        PARK_LANDMARKS.paddockGate.y - 28,
        "effect-electric-spark-sequence",
        0,
      )
      .setDepth(4.75)
      .setVisible(false);

    for (let index = 0; index < 4; index += 1) {
      this.add
        .image(
          PARK_LANDMARKS.tourRoute.x - 250 + index * 150,
          PARK_LANDMARKS.tourRoute.y + Math.sin(index * 1.7) * 22,
          "decal-tire-tracks",
        )
        .setDepth(0.72)
        .setAlpha(0.62)
        .setRotation(-0.03 + index * 0.012);
    }
    for (let index = 0; index < 5; index += 1) {
      this.add
        .image(
          PARK_LANDMARKS.paddockGate.x - 330 + index * 105,
          PARK_LANDMARKS.paddockGate.y + 150 + index * 72,
          "decal-giant-footprint",
        )
        .setDepth(0.74)
        .setAlpha(0.7)
        .setRotation(0.38 + (index % 2 === 0 ? -0.12 : 0.1));
    }
  }

  create(): void {
    this.cameras.main.setBackgroundColor("#0d1a10");
    this.cameras.main.setBounds(0, 0, ARENA_WIDTH, ARENA_HEIGHT);
    this.cameras.main.centerOn(ARENA_WIDTH / 2, ARENA_HEIGHT / 2);
    this.syncResponsiveCamera();
    this.scale.on(Phaser.Scale.Events.RESIZE, () => this.syncResponsiveCamera());
    this.createDinoAnims();
    this.plantJungle();
    this.amberCacheSprite = this.add
      .image(AMBER_CACHE_X, AMBER_CACHE_Y, "objective-amber-cache")
      .setDepth(3.8);
    this.baitPenSprite = this.add
      .image(BAIT_PEN_X, BAIT_PEN_Y, "objective-bait-pen")
      .setDepth(3.8);
    this.goatSprite = this.add
      .image(BAIT_PEN_X, BAIT_PEN_Y - 2, "goat")
      .setScale(2)
      .setDepth(3.9);
    this.rallyFlareSprite = this.add
      .image(0, 0, "rally-flare")
      .setDepth(5.6)
      .setVisible(false);
    this.rallySmokeSprite = this.add
      .sprite(0, 0, "effect-rally-smoke-sequence", 0)
      .setDepth(5.5)
      .setVisible(false);
    this.drawArena();
    this.arenaBoundaryLayer = this.add.graphics().setDepth(2);
    this.drawActiveArenaBoundary();
    this.hazardLayer = this.add.graphics().setDepth(3);
    this.effectLayer = this.add.graphics().setDepth(7);
    this.game.canvas.tabIndex = 0;
    this.game.canvas.addEventListener("pointerdown", () => focusGameControls());

    if (!this.input.keyboard) throw new Error("Keyboard input unavailable");
    this.cursors = this.input.keyboard.createCursorKeys();
    this.wasd = this.input.keyboard.addKeys(
      {
        up: Phaser.Input.Keyboard.KeyCodes.W,
        down: Phaser.Input.Keyboard.KeyCodes.S,
        left: Phaser.Input.Keyboard.KeyCodes.A,
        right: Phaser.Input.Keyboard.KeyCodes.D,
      },
      false,
    ) as typeof this.wasd;
    const space = this.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.SPACE,
      false,
    );
    space.on("down", () => {
      this.fireLootBomb();
    });
    const shift = this.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.SHIFT,
      false,
    );
    shift.on("down", () => {
      this.startLocalDash();
    });
    this.input.keyboard.clearCaptures();

    connection.addEventListener("server", (event) => {
      const message = (event as CustomEvent<ServerMessage>).detail;
      if (message.t === "snapshot") this.applySnapshot(message);
      if (message.t === "pickup") this.showPickupCallout(message);
    });
  }

  private showPickupCallout(message: PickupMessage): void {
    const player = this.players.get(connection.localId);
    if (!player) {
      this.pendingPickupCallout = message;
      return;
    }
    if (player.ghost) return;
    this.pendingPickupCallout = null;
    this.pickupCallout?.destroy();
    const callout = this.add
      .text(player.body.x, player.body.y - 54, message.name + "\n" + message.effect, {
        fontFamily: "DM Mono, monospace",
        fontStyle: "bold",
        fontSize: "11px",
        color: message.color,
        align: "center",
        backgroundColor: "rgba(10, 20, 13, 0.9)",
        padding: { x: 8, y: 6 },
        stroke: "#08120c",
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setDepth(10);
    this.pickupCallout = callout;
    this.tweens.add({
      targets: callout,
      y: callout.y - 48,
      alpha: 0,
      duration: 1_150,
      hold: 650,
      ease: "Cubic.easeOut",
      onComplete: () => {
        if (this.pickupCallout === callout) this.pickupCallout = null;
        callout.destroy();
      },
    });
  }

  private animateEnvironment(time: number): void {
    for (const landmark of this.animatedLandmarks) {
      const wave = 0.5 + Math.sin(time / 520 + landmark.phase) * 0.5;
      landmark.image.clearTint();
      if (landmark.kind === "research") {
        landmark.image
          .setAlpha(0.86 + wave * 0.14)
          .setScale(1 + wave * 0.025)
          .setTint(wave > 0.8 ? 0xffd66b : 0xffffff);
      } else if (landmark.kind === "incubator") {
        const flicker = Math.sin(time / 74 + landmark.phase) > 0.62;
        landmark.image
          .setAlpha(flicker ? 0.72 : 1)
          .setScale(1)
          .setTint(flicker ? 0x9fe8ff : 0xffffff);
      } else {
        landmark.image
          .setAlpha(1)
          .setScale(1)
          .setRotation(
            landmark.baseRotation + Math.sin(time / 900 + landmark.phase) * 0.025,
          );
      }
    }

    const sparkCycle = (time + 640) % 4_200;
    this.landmarkSparkSprite
      .setVisible(sparkCycle < 420)
      .setFrame(Math.min(3, Math.floor(sparkCycle / 105)))
      .setRotation(Math.sin(time / 130) * 0.18)
      .setAlpha(sparkCycle < 420 ? 0.86 : 0);
  }

  private syncResponsiveCamera(): void {
    const touchMinimumZoom =
      touchInput && this.scale.height > this.scale.width ? 0.82 : 0.9;
    const zoom = Math.max(
      touchInput ? touchMinimumZoom : 1,
      Math.min(
        this.scale.width / VIEWPORT_WIDTH,
        this.scale.height / VIEWPORT_HEIGHT,
      ),
    );
    this.cameras.main.setZoom(zoom);
  }

  update(time: number, delta: number): void {
    this.sendInput(time);
    this.predictLocalPlayer(time, delta);
    this.interpolateViews(time, delta);
    this.animateEnvironment(time);
    this.drawHazards();
    this.drawCombatEffects(time);
    updateSpecialHud();
    this.updateTouchDashHud(time);
  }

  private drawArena(): void {
    const graphics = this.add.graphics();
    graphics.fillStyle(0x100d1d, 1);
    graphics.fillRect(0, 0, ARENA_WIDTH, ARENA_HEIGHT);

    graphics.lineStyle(1, 0x493c68, 0.22);
    for (let x = 0; x <= ARENA_WIDTH; x += 80) {
      graphics.lineBetween(x, 0, x, ARENA_HEIGHT);
    }
    for (let y = 0; y <= ARENA_HEIGHT; y += 80) {
      graphics.lineBetween(0, y, ARENA_WIDTH, y);
    }

    graphics.lineStyle(3, 0x7253b0, 0.2);
    for (let x = VIEWPORT_WIDTH; x < ARENA_WIDTH; x += VIEWPORT_WIDTH) {
      graphics.lineBetween(x, 0, x, ARENA_HEIGHT);
    }
    for (let y = VIEWPORT_HEIGHT; y < ARENA_HEIGHT; y += VIEWPORT_HEIGHT) {
      graphics.lineBetween(0, y, ARENA_WIDTH, y);
    }

    graphics.fillStyle(0xff4f70, 0.055);
    graphics.fillRect(0, 0, ARENA_WIDTH, 34);
    graphics.fillRect(0, ARENA_HEIGHT - 34, ARENA_WIDTH, 34);
    graphics.fillRect(0, 0, 34, ARENA_HEIGHT);
    graphics.fillRect(ARENA_WIDTH - 34, 0, 34, ARENA_HEIGHT);
    graphics.lineStyle(16, 0xf4bc62, 0.22);
    graphics.strokeRoundedRect(12, 12, ARENA_WIDTH - 24, ARENA_HEIGHT - 24, 28);
    graphics.lineStyle(5, 0xff7189, 0.88);
    graphics.strokeRoundedRect(22, 22, ARENA_WIDTH - 44, ARENA_HEIGHT - 44, 22);
    graphics.lineStyle(2, 0xffe3a0, 0.78);
    graphics.strokeRoundedRect(31, 31, ARENA_WIDTH - 62, ARENA_HEIGHT - 62, 18);
  }

  private applyArenaBounds(
    arena: [number, number, number, number],
  ): void {
    if (arena.every((value, index) => value === this.activeArena[index])) return;
    this.activeArena = [...arena];
    const [x, y, width, height] = this.activeArena;
    this.cameras.main.setBounds(x, y, width, height);
    this.drawActiveArenaBoundary();
  }

  private drawActiveArenaBoundary(): void {
    if (!this.arenaBoundaryLayer) return;
    const [x, y, width, height] = this.activeArena;
    this.arenaBoundaryLayer.clear();
    this.arenaBoundaryLayer.fillStyle(0xff4968, 0.12);
    this.arenaBoundaryLayer.fillRect(x, y, width, 26);
    this.arenaBoundaryLayer.fillRect(x, y + height - 26, width, 26);
    this.arenaBoundaryLayer.fillRect(x, y, 26, height);
    this.arenaBoundaryLayer.fillRect(x + width - 26, y, 26, height);
    this.arenaBoundaryLayer.lineStyle(14, 0xff5d79, 0.38);
    this.arenaBoundaryLayer.strokeRoundedRect(
      x + 7,
      y + 7,
      width - 14,
      height - 14,
      22,
    );
    this.arenaBoundaryLayer.lineStyle(4, 0xffd66b, 0.92);
    this.arenaBoundaryLayer.strokeRoundedRect(
      x + 18,
      y + 18,
      width - 36,
      height - 36,
      16,
    );
  }

  private sendInput(time: number): void {
    let x = mobileMovement.x + arenaTouch.x;
    let y = mobileMovement.y + arenaTouch.y;
    const localView = this.players.get(connection.localId);
    if (
      arenaTouch.targetX !== null &&
      arenaTouch.targetY !== null &&
      localView &&
      !localView.ghost
    ) {
      const dx = arenaTouch.targetX - localView.body.x;
      const dy = arenaTouch.targetY - localView.body.y;
      const targetDistance = Math.hypot(dx, dy);
      if (targetDistance <= 24) {
        arenaTouch.targetX = null;
        arenaTouch.targetY = null;
      } else {
        x += dx / targetDistance;
        y += dy / targetDistance;
      }
    }
    if (this.cursors.left.isDown || this.wasd.left.isDown) x -= 1;
    if (this.cursors.right.isDown || this.wasd.right.isDown) x += 1;
    if (this.cursors.up.isDown || this.wasd.up.isDown) y -= 1;
    if (this.cursors.down.isDown || this.wasd.down.isDown) y += 1;

    const length = Math.hypot(x, y) || 1;
    this.inputX = x / Math.max(1, length);
    this.inputY = y / Math.max(1, length);

    if (
      this.inputX !== this.lastInputX ||
      this.inputY !== this.lastInputY ||
      time - this.lastInputAt > 250
    ) {
      this.lastInputX = this.inputX;
      this.lastInputY = this.inputY;
      this.lastInputAt = time;
      connection.send({
        t: "input",
        x: this.inputX,
        y: this.inputY,
        seq: ++this.inputSeq,
      });
    }
  }

  private applySnapshot(snapshot: SnapshotMessage): void {
    this.snapshot = snapshot;
    this.applyArenaBounds(snapshot.arena);
    for (const effect of snapshot.combatFx) {
      if (
        !this.combatFx.has(effect[0]) &&
        effect[1] === "special" &&
        effect[2] === connection.localId
      ) {
        this.cameras.main.shake(220, 0.006);
      }
      if (!this.combatFx.has(effect[0]) && effect[1] === "raid") {
        this.cameras.main.shake(1_100, 0.022);
        flashRaidUltimate();
      }
      this.combatFx.set(effect[0], effect);
    }
    const seenPlayers = new Set<string>();
    for (const tuple of snapshot.players) {
      seenPlayers.add(tuple[0]);
      let view = this.players.get(tuple[0]);
      if (!view) {
        view = this.createPlayerView(tuple);
        this.players.set(tuple[0], view);
      }
      this.updatePlayerView(view, tuple);
    }
    for (const [id, view] of this.players) {
      if (seenPlayers.has(id)) continue;
      destroyPlayerView(view);
      this.players.delete(id);
    }
    if (this.pendingPickupCallout && this.players.has(connection.localId)) {
      this.showPickupCallout(this.pendingPickupCallout);
    }
    this.updateCameraFollow();

    const seenEnemies = new Set<number>();
    for (const tuple of snapshot.enemies) {
      seenEnemies.add(tuple[0]);
      let view = this.enemies.get(tuple[0]);
      if (!view) {
        view = this.createEnemyView(tuple);
        this.enemies.set(tuple[0], view);
      }
      this.updateEnemyView(view, tuple);
    }
    for (const [id, view] of this.enemies) {
      if (seenEnemies.has(id)) continue;
      this.deathBursts.push({
        x: view.body.x,
        y: view.body.y,
        color: enemyColor(view.kind),
        createdAt: this.time.now,
        kind: view.kind,
      });
      if (this.deathBursts.length > 60) this.deathBursts.shift();
      destroyEnemyView(view);
      this.enemies.delete(id);
    }

    const seenSummons = new Set<number>();
    for (const tuple of snapshot.summons) {
      seenSummons.add(tuple[0]);
      let view = this.summons.get(tuple[0]);
      if (!view) {
        view = this.createSummonView(tuple);
        this.summons.set(tuple[0], view);
      }
      this.updateSummonView(view, tuple);
    }
    for (const [id, view] of this.summons) {
      if (seenSummons.has(id)) continue;
      this.deathBursts.push({
        x: view.body.x,
        y: view.body.y,
        color: ABILITY_COLORS.necro,
        createdAt: this.time.now,
      });
      destroySummonView(view);
      this.summons.delete(id);
    }

    const seenPowerUps = new Set<number>();
    for (const tuple of snapshot.powerUps) {
      seenPowerUps.add(tuple[0]);
      let view = this.powerUps.get(tuple[0]);
      if (!view) {
        view = this.createPowerUpView(tuple);
        this.powerUps.set(tuple[0], view);
      }
      view.expiresAt = tuple[4];
    }
    for (const [id, view] of this.powerUps) {
      if (seenPowerUps.has(id)) continue;
      view.orb.destroy();
      view.icon.destroy();
      this.powerUps.delete(id);
    }
  }

  private updateCameraFollow(): void {
    const localId = connection.localId;
    const localView = this.players.get(localId);
    if (!localView) {
      if (this.followedPlayerId) this.cameras.main.stopFollow();
      this.followedPlayerId = "";
      return;
    }
    if (this.followedPlayerId === localId) return;
    this.cameras.main.stopFollow();
    this.cameras.main.startFollow(localView.body, true, 0.28, 0.28);
    this.followedPlayerId = localId;
  }

  private createPowerUpView(tuple: PowerUpTuple): PowerUpView {
    const [, kind, x, y, expiresAt] = tuple;
    const color = powerUpColor(kind);
    const rarePickup = kind === "rod" || kind === "barrage";
    const orb = this.add
      .circle(x, y, rarePickup ? 26 : 21, color, 0.22)
      .setStrokeStyle(rarePickup ? 5 : 3, color, 0.95)
      .setDepth(8);
    const art = POWERUP_ART[kind];
    const icon = art
      ? this.add.image(x, y, art).setScale(rarePickup ? 1.4 : 1.1).setDepth(9)
      : this.add
          .text(x, y, powerUpGlyph(kind), {
            fontFamily: "Archivo Black, system-ui, sans-serif",
            fontStyle: "bold",
            fontSize: rarePickup ? "22px" : "18px",
            color: "#fffdf3",
            stroke: "#0c130d",
            strokeThickness: 4,
          })
          .setOrigin(0.5)
          .setDepth(9);
    return { orb, icon, kind, expiresAt };
  }

  private createPlayerView(tuple: PlayerTuple): PlayerView {
    const [id, name, x, y, , , level, ability] = tuple;
    const skin = tuple[17] || "jham";
    const body = this.add
      .circle(x, y, 18, ABILITY_COLORS[ability], 0.16)
      .setDepth(6);
    const sprite = this.add
      .sprite(x, y - 12, skin, 0)
      .setScale(1)
      .setDepth(7);
    const outline = this.add
      .circle(x, y, 23, 0xffffff, 0)
      .setStrokeStyle(id === connection.localId ? 4 : 2, 0xffffff, id === connection.localId ? 1 : 0.2)
      .setDepth(5);
    const nameText = this.add
      .text(x, y - 58, name, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "13px",
        color: "#f8f2ff",
        stroke: "#0c130d",
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(8);
    const evolutionText = this.add
      .text(x, y - 40, "", {
        fontFamily: "DM Mono, monospace",
        fontStyle: "bold",
        fontSize: "9px",
        color: "#ffd66b",
        stroke: "#0c130d",
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(8)
      .setVisible(false);
    const levelText = this.add
      .text(x + 17, y + 13, String(level), {
        fontFamily: "system-ui, sans-serif",
        fontStyle: "bold",
        fontSize: "10px",
        color: "#0c130d",
        backgroundColor: "#f9c80e",
        padding: { x: 3, y: 1 },
      })
      .setOrigin(0.5)
      .setDepth(9);
    const rune = this.add
      .text(x, y, abilityGlyph(ability), {
        fontFamily: "system-ui, sans-serif",
        fontStyle: "bold",
        fontSize: "16px",
        color: "#160f25",
      })
      .setOrigin(0.5)
      .setDepth(8);
    const revive = this.add
      .text(x, y + 35, "REVIVE", {
        fontFamily: "DM Mono, monospace",
        fontStyle: "bold",
        fontSize: "10px",
        color: "#9fe8ff",
        stroke: "#0c130d",
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(9)
      .setVisible(false);

    return {
      body,
      sprite,
      skin,
      outline,
      name: nameText,
      evolution: evolutionText,
      level: levelText,
      rune,
      revive,
      targetX: x,
      targetY: y,
      ability,
      ghost: false,
      reviveProgress: 0,
      reviveShieldUntil: 0,
      inspiredUntil: 0,
      range: 1,
    };
  }

  private updatePlayerView(view: PlayerView, tuple: PlayerTuple): void {
    const [
      id,
      name,
      x,
      y,
      hp,
      maxHp,
      level,
      ability,
      ghost,
      ,
      reviveProgress,
      range,
      reviveShieldUntil,
      inspiredUntil,
      rodUntil,
      pickupShieldUntil,
      evolutionName,
    ] = tuple;
    view.targetX = x;
    view.targetY = y;
    if (id === connection.localId) {
      const error = Phaser.Math.Distance.Between(view.body.x, view.body.y, x, y);
      const correction = error > 120 ? 1 : 0.2;
      view.body.x = Phaser.Math.Linear(view.body.x, x, correction);
      view.body.y = Phaser.Math.Linear(view.body.y, y, correction);
    }
    const tupleSkin = tuple[17] || "jham";
    if (tupleSkin !== view.skin) {
      view.skin = tupleSkin;
      view.sprite.setTexture(tupleSkin, 0);
      view.sprite.setScale(1);
      view.sprite.anims.stop();
    }
    view.ability = ability;
    view.ghost = ghost === 1;
    view.reviveProgress = reviveProgress;
    view.reviveShieldUntil = reviveShieldUntil;
    view.inspiredUntil = inspiredUntil;
    view.range = range;
    view.body.setFillStyle(ABILITY_COLORS[ability], ghost ? 0.06 : 0.16);
    view.body.setScale(0.85 + 0.15 * Math.max(0, hp / maxHp));
    view.sprite.setAlpha(ghost ? 0.35 : 1);
    view.outline.setStrokeStyle(
      rodUntil > Date.now() + connection.serverOffset
        ? 7
        : id === connection.localId
          ? 4
          : 2,
      ghost
        ? 0x9fe8ff
        : rodUntil > Date.now() + connection.serverOffset
          ? 0xff7b36
          : pickupShieldUntil > Date.now() + connection.serverOffset
            ? 0x82a7ff
            : 0xffffff,
      id === connection.localId ||
        rodUntil > Date.now() + connection.serverOffset ||
        pickupShieldUntil > Date.now() + connection.serverOffset
        ? 1
        : ghost
          ? 0.45
          : 0.18,
    );
    view.name.setText(name).setAlpha(ghost ? 0.5 : 1);
    view.evolution
      .setText(evolutionName)
      .setVisible(Boolean(evolutionName))
      .setAlpha(ghost ? 0.5 : 1);
    view.level.setText(String(level)).setAlpha(ghost ? 0.5 : 1);
    view.rune.setText(ghost ? "✦" : abilityGlyph(ability)).setAlpha(ghost ? 0.7 : 1);
    view.revive.setText(
      reviveProgress > 0
        ? "REVIVING " + Math.round(reviveProgress * 100) + "%"
        : id === connection.localId
          ? "ALLY: STAND HERE 3s"
          : "REVIVE",
    );
  }

  private createEnemyView(tuple: EnemyTuple): EnemyView {
    const [enemyId, kind, x, y, hpRatio, size, disguiseName, disguiseAbility] = tuple;
    const color = enemyColor(kind);
    const body =
      kind === "gelatinous"
        ? this.add
            .rectangle(x, y, size * 2, size * 2, color, 0.82)
            .setDepth(4)
        : kind === "boss" || kind === "miniboss" || kind === "weakpoint"
        ? this.add
            .star(
              x,
              y,
              kind === "boss" ? 12 : kind === "weakpoint" ? 4 : 7,
              size * (kind === "weakpoint" ? 0.42 : 0.68),
              size,
              color,
              1,
            )
            .setDepth(4)
        : this.add
            .circle(
              x,
              y,
              size,
              kind === "imposter" && disguiseAbility
                ? ABILITY_COLORS[disguiseAbility]
                : color,
              1,
            )
            .setDepth(4);
    if (kind === "boss") body.setStrokeStyle(8, 0xffd66b, 0.8);
    if (kind === "weakpoint") body.setStrokeStyle(6, 0xffffff, 0.94);
    if (kind === "miniboss") body.setStrokeStyle(5, 0xf2b0ff, 0.9);
    if (kind === "guardian") body.setStrokeStyle(6, 0x9effdf, 0.9);
    if (kind === "skitter") body.setStrokeStyle(3, 0xf5ff8a, 0.9);
    if (kind === "bulwark") body.setStrokeStyle(6, 0xffd66b, 0.92);
    if (kind === "stalker") body.setStrokeStyle(4, 0xff6a96, 0.92);
    if (kind === "hexweaver") body.setStrokeStyle(4, 0xd68cff, 0.92);
    if (kind === "imposter") body.setStrokeStyle(3, 0xffffff, 0.22);
    if (kind === "arcspinner") body.setStrokeStyle(5, 0x73efff, 0.92);
    if (kind === "veilkeeper") body.setStrokeStyle(5, 0x9d94c8, 0.9);
    if (kind === "mirecaller") body.setStrokeStyle(5, 0xd77cff, 0.9);
    if (kind === "gravemaw") body.setStrokeStyle(6, 0xff9d5c, 0.94);
    if (kind === "nullbell") body.setStrokeStyle(5, 0xf5f1ff, 0.92);
    if (kind === "shardmother") body.setStrokeStyle(6, 0x7cf5ff, 0.94);
    if (kind === "gelatinous") body.setStrokeStyle(8, 0xb5ff78, 0.92);
    let dino = dinoForKind(kind);
    if (kind === "miniboss") {
      const species = [
        { key: "carno", left: "carno-left", right: "carno-right" },
        { key: "spino", left: "spino-left", right: "spino-right" },
      ][enemyId % 2];
      dino = species;
    }
    const sprite = kind === "weakpoint"
      ? this.add
          .sprite(x, y, "tranq-canister", 0)
          .setScale(1.25)
          .setDepth(5)
      : dino
      ? this.add
          .sprite(x, y, dino.key, 0)
          .setDepth(kind === "boss" ? 6 : 5)
      : null;
    if (sprite && dino) {
      sprite.setScale(
        enemySpriteWorldWidth(kind, dino.key, size, sprite.width) /
          sprite.width,
      );
    }
    let baseAlpha = 1;
    if (kind === "weakpoint") {
      baseAlpha = 0;
      body.setAlpha(0);
    }
    if (sprite && dino) {
      if (dino.left && dino.left !== "jham-idle") sprite.play(dino.left);
      // the shape stays only as a faint aura under bosses; dinos carry the look
      baseAlpha = kind === "boss" || kind === "miniboss" ? 0.3 : 0;
      body.setAlpha(baseAlpha);
    }
    const healthBg = this.add
      .rectangle(x, y - size - 12, Math.max(24, size * 1.8), 5, 0x160f25, 0.9)
      .setDepth(6);
    const health = this.add
      .rectangle(
        x,
        y - size - 12,
        Math.max(24, size * 1.8) * hpRatio,
        5,
        kind === "gelatinous"
          ? 0x9bff70
          : kind === "guardian"
          ? 0x62e8c2
          : kind === "miniboss"
            ? 0xe19aff
            : isNamedEnemy(kind)
              ? 0xff9fe2
            : 0xf4bc62,
        1,
      )
      .setOrigin(0, 0.5)
      .setDepth(7);
    const label = isNamedEnemy(kind) || kind === "gelatinous" || kind === "weakpoint"
      ? this.add
          .text(
            x,
            y - size - 25,
            kind === "gelatinous"
              ? "ANKYLOSAURUS · TOUCH = DEATH"
              : kind === "weakpoint"
                ? "BREAK TRANQ CANISTER"
              : kind === "imposter" && disguiseName
                ? disguiseName
                : namedEnemyName(kind),
            {
            fontFamily: "DM Mono, monospace",
            fontStyle: "bold",
            fontSize: kind === "gelatinous" ? "14px" : "11px",
            color:
              kind === "gelatinous"
                ? "#dbffc4"
                : kind === "weakpoint"
                  ? "#fff2a8"
                  : "#fff0fb",
            stroke: "#170d23",
            strokeThickness: 4,
            },
          )
          .setOrigin(0.5)
          .setDepth(8)
      : null;
    const rune =
      kind === "imposter" && disguiseAbility
        ? this.add
            .text(x, y, abilityGlyph(disguiseAbility), {
              fontFamily: "system-ui, sans-serif",
              fontStyle: "bold",
              fontSize: "16px",
              color: "#160f25",
            })
            .setOrigin(0.5)
            .setDepth(8)
        : null;
    return {
      body,
      sprite,
      dinoAnims:
        dino && dino.left && dino.right && dino.left !== dino.right
          ? { left: dino.left, right: dino.right }
          : null,
      baseAlpha,
      healthBg,
      health,
      label,
      rune,
      targetX: x,
      targetY: y,
      kind,
      hpRatio,
      size,
    };
  }

  private updateEnemyView(view: EnemyView, tuple: EnemyTuple): void {
    const [
      ,
      kind,
      x,
      y,
      hpRatio,
      size,
      disguiseName,
      disguiseAbility,
      revealedUntil,
    ] = tuple;
    view.targetX = x;
    view.targetY = y;
    view.hpRatio = hpRatio;
    view.size = size;
    if (
      (kind === "boss" || kind === "miniboss" || kind === "weakpoint") &&
      view.body instanceof Phaser.GameObjects.Star
    ) {
      view.body.rotation +=
        kind === "boss" ? 0.025 : kind === "weakpoint" ? 0.085 : 0.055;
    }
    if (kind === "gelatinous") {
      view.body.rotation = Math.sin(this.time.now / 520) * 0.035;
    }
    if (kind === "imposter") {
      const revealed = revealedUntil > Date.now() + connection.serverOffset;
      view.body.setFillStyle(
        revealed || !disguiseAbility
          ? enemyColor("imposter")
          : ABILITY_COLORS[disguiseAbility],
        1,
      );
      view.body.setStrokeStyle(
        revealed ? 5 : 3,
        revealed ? 0xff254f : 0xffffff,
        revealed ? 1 : 0.22,
      );
      view.label?.setText(revealed ? "COMPANY MOLE — EXPOSED" : disguiseName);
      if (view.sprite) {
        if (view.sprite.texture.key !== "jham") {
          view.sprite.setTexture("jham", 0);
          view.sprite.anims.stop();
          view.sprite.setScale(1);
          view.dinoAnims = null;
        }
        view.sprite.setTint(revealed ? 0xff6d72 : 0xffffff);
      }
      view.rune
        ?.setText(revealed || !disguiseAbility ? "!" : abilityGlyph(disguiseAbility))
        .setColor(revealed ? "#fff4f6" : "#160f25");
    }
  }

  private createSummonView(tuple: SummonTuple): SummonView {
    const [, kind, x, y, size, , expiresAt] = tuple;
    const body = this.add
      .circle(x, y, size, enemyColor(kind), 0.1)
      .setDepth(5);
    // Hatchery Tech allies render as lab-green compys.
    const sprite = this.add
      .sprite(x, y, "compy", 0)
      .setScale(Math.max(0.9, (size * 2.6) / 48))
      .setTint(0xa8ffa0)
      .setDepth(5);
    sprite.play("compy-left");
    const outline = this.add
      .circle(x, y, size + 4, ABILITY_COLORS.necro, 0.06)
      .setStrokeStyle(3, ABILITY_COLORS.necro, 0.92)
      .setDepth(5);
    const rune = this.add
      .text(x, y, "◉", {
        fontFamily: "system-ui, sans-serif",
        fontStyle: "bold",
        fontSize: Math.max(12, Math.min(20, size * 0.85)) + "px",
        color: "#d8ffe3",
        stroke: "#15301f",
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setDepth(6);
    return {
      body,
      sprite,
      outline,
      rune,
      targetX: x,
      targetY: y,
      kind,
      size,
      expiresAt,
    };
  }

  private updateSummonView(view: SummonView, tuple: SummonTuple): void {
    const [, kind, x, y, size, , expiresAt] = tuple;
    view.targetX = x;
    view.targetY = y;
    view.kind = kind;
    view.size = size;
    view.expiresAt = expiresAt;
  }

  private predictLocalPlayer(time: number, delta: number): void {
    const view = this.players.get(connection.localId);
    if (!view || view.ghost) return;
    const dashing = time < this.localDashUntil;
    const directionX = dashing ? this.localDashX : this.inputX;
    const directionY = dashing ? this.localDashY : this.inputY;
    const serverNow = Date.now() + connection.serverOffset;
    const inspiredSpeed =
      playerState && serverNow < playerState.inspiredUntil ? 1.18 : 1;
    const pickupSpeed = temporaryMoveSpeedMultiplier(
      Boolean(playerState && serverNow < playerState.hasteUntil),
      Boolean(playerState && serverNow < playerState.energyBarUntil),
      Boolean(playerState && serverNow < playerState.rodUntil),
    );
    const speed =
      190 *
      (playerState?.moveSpeed ?? 1) *
      inspiredSpeed *
      pickupSpeed *
      (dashing ? 3.1 : 1);
    const dt = Math.min(delta, 50) / 1_000;
    const [arenaX, arenaY, arenaWidth, arenaHeight] = this.activeArena;
    view.body.x = Phaser.Math.Clamp(
      view.body.x + directionX * speed * dt,
      arenaX + 24,
      arenaX + arenaWidth - 24,
    );
    view.body.y = Phaser.Math.Clamp(
      view.body.y + directionY * speed * dt,
      arenaY + 24,
      arenaY + arenaHeight - 24,
    );
  }

  private interpolateViews(time: number, delta: number): void {
    const playerFactor = 1 - Math.exp(-Math.min(delta, 50) / 78);
    const enemyFactor = 1 - Math.exp(-Math.min(delta, 50) / 92);
    const localView = this.players.get(connection.localId);
    for (const [id, view] of this.players) {
      if (id !== connection.localId) {
        view.body.x = Phaser.Math.Linear(view.body.x, view.targetX, playerFactor);
        view.body.y = Phaser.Math.Linear(view.body.y, view.targetY, playerFactor);
      }
      const localDashing = id === connection.localId && time < this.localDashUntil;
      const movementX =
        id === connection.localId
          ? localDashing
            ? this.localDashX
            : this.inputX
          : view.targetX - view.body.x;
      const movementY =
        id === connection.localId
          ? localDashing
            ? this.localDashY
            : this.inputY
          : view.targetY - view.body.y;
      const moving =
        !view.ghost &&
        Math.hypot(movementX, movementY) >
          (id === connection.localId ? 0.05 : 0.6);
      const motion = survivorMotionFrame(movementX, movementY, moving);
      view.sprite.play(view.skin + "-" + motion.animation, true);
      // Every survivor atlas uses a left-facing side row. Right travel mirrors it.
      view.sprite.setFlipX(motion.flipX);
      view.sprite.setPosition(
        view.body.x,
        view.body.y - 12,
      );
      view.outline.setPosition(view.body.x, view.body.y);
      view.name.setPosition(
        view.body.x,
        view.body.y - 58,
      );
      view.evolution.setPosition(
        view.body.x,
        view.body.y - 40,
      );
      view.level.setPosition(view.body.x + 17, view.body.y + 13);
      view.rune.setPosition(view.body.x, view.body.y + 24);
      view.revive.setPosition(view.body.x, view.body.y + 35);
      const nearLocal =
        localView !== undefined &&
        Phaser.Math.Distance.Between(
          localView.body.x,
          localView.body.y,
          view.body.x,
          view.body.y,
        ) <= 180;
      view.revive.setVisible(
        view.ghost && (id === connection.localId || nearLocal || view.reviveProgress > 0),
      );
    }
    for (const view of this.enemies.values()) {
      view.body.x = Phaser.Math.Linear(view.body.x, view.targetX, enemyFactor);
      view.body.y = Phaser.Math.Linear(view.body.y, view.targetY, enemyFactor);
      if (view.sprite) {
        const stepX = view.body.x - view.sprite.x;
        const moving = Math.abs(stepX) > 0.25;
        if (view.dinoAnims && moving) {
          view.sprite.play(stepX < 0 ? view.dinoAnims.left : view.dinoAnims.right, true);
        }
        if (!view.dinoAnims && moving && isIllustratedNamedEnemy(view.kind)) {
          view.sprite.setFlipX(stepX < 0);
        }
        if (!moving && view.dinoAnims && localView) {
          const towardPlayer = localView.body.x < view.body.x;
          const playerDistance = Phaser.Math.Distance.Between(
            localView.body.x,
            localView.body.y,
            view.body.x,
            view.body.y,
          );
          if (view.dinoAnims.left === "dilo-left" && playerDistance < 175) {
            view.sprite.play(towardPlayer ? "dilo-spit-left" : "dilo-spit-right", true);
          } else if (view.kind === "boss" && playerDistance < view.size + 55) {
            view.sprite.play(towardPlayer ? "trex-bite-left" : "trex-bite-right", true);
          }
        }
        view.sprite.setPosition(view.body.x, view.body.y - view.size * 0.2);
      }
      const width = Math.max(24, view.size * 1.8);
      view.healthBg.setPosition(view.body.x, view.body.y - view.size - 12).setSize(width, 5);
      view.health
        .setPosition(view.body.x - width / 2, view.body.y - view.size - 12)
        .setSize(width * view.hpRatio, 5);
      view.label?.setPosition(view.body.x, view.body.y - view.size - 25);
      view.rune?.setPosition(view.body.x, view.body.y);
      const showHealth =
        view.hpRatio < 0.999 ||
        view.kind === "boss" ||
        view.kind === "miniboss" ||
        view.kind === "guardian" ||
        view.kind === "gelatinous" ||
        view.kind === "weakpoint" ||
        isNamedEnemy(view.kind);
      view.healthBg.setVisible(showHealth);
      view.health.setVisible(showHealth);
    }
    const serverNow = Date.now() + connection.serverOffset;
    for (const view of this.summons.values()) {
      view.body.x = Phaser.Math.Linear(view.body.x, view.targetX, enemyFactor);
      view.body.y = Phaser.Math.Linear(view.body.y, view.targetY, enemyFactor);
      const stepX = view.body.x - view.sprite.x;
      if (Math.abs(stepX) > 0.25) {
        view.sprite.play(stepX < 0 ? "compy-left" : "compy-right", true);
      }
      view.sprite.setPosition(view.body.x, view.body.y);
      view.outline.setPosition(view.body.x, view.body.y);
      view.rune.setPosition(view.body.x, view.body.y + 16);
      const expiryAlpha = Phaser.Math.Clamp(
        (view.expiresAt - serverNow) / 1_500,
        0.18,
        1,
      );
      view.body.setAlpha(0.1 * expiryAlpha);
      view.sprite.setAlpha(expiryAlpha);
      view.outline.setAlpha(expiryAlpha);
      view.rune.setAlpha(expiryAlpha);
    }
    for (const [id, view] of this.powerUps) {
      const pulse = 1 + Math.sin(this.time.now / 130 + id) * 0.1;
      const expiryAlpha = Phaser.Math.Clamp(
        (view.expiresAt - serverNow) / 1_500,
        0.2,
        1,
      );
      view.orb.setScale(pulse).setAlpha(expiryAlpha);
      view.icon
        .setScale(view.kind === "rod" ? pulse * 1.08 : pulse)
        .setAlpha(expiryAlpha);
    }
  }

  private drawHazards(): void {
    this.hazardLayer.clear();
    let stampedeUsed = 0;
    let hatchUsed = 0;
    let cableUsed = 0;
    let sparkUsed = 0;
    let bogUsed = 0;
    let stompUsed = 0;
    let dustUsed = 0;
    for (const herd of this.stampedeSprites) herd.setVisible(false);
    for (const hatch of this.hatchSprites) hatch.setVisible(false);
    for (const cable of this.cableSprites) cable.setVisible(false);
    for (const spark of this.sparkSprites) spark.setVisible(false);
    for (const bog of this.bogSprites) bog.setVisible(false);
    for (const cracks of this.stompCrackSprites) cracks.setVisible(false);
    for (const dust of this.dustBurstSprites) dust.setVisible(false);
    for (const enemy of this.enemies.values()) {
      if (enemy.kind === "nullbell") enemy.sprite?.clearTint();
    }
    this.rallyFlareSprite.setVisible(false);
    this.rallySmokeSprite.setVisible(false);
    if (!this.snapshot) return;
    const [arenaX, arenaY, arenaWidth, arenaHeight] = this.activeArena;
    const now = Date.now() + connection.serverOffset;
    const [banked, required] = this.snapshot.moteBank;
    const bankProgress = Phaser.Math.Clamp(banked / Math.max(1, required), 0, 1);
    const [baited, baitRequired] = this.snapshot.baitBank;
    const baitProgress = Phaser.Math.Clamp(
      baited / Math.max(1, baitRequired),
      0,
      1,
    );
    const guardianAlive = this.snapshot.enemies.some((enemy) => enemy[1] === "guardian");
    const baitPenLocked =
      guardianAlive || this.snapshot.overdriveDrop !== null || now < this.snapshot.overdriveUntil;
    this.goatSprite.setVisible(!baitPenLocked);
    this.amberCacheSprite.setScale(1 + bankProgress * 0.035);
    this.baitPenSprite
      .setScale(1 + baitProgress * 0.035)
      .setAlpha(baitPenLocked ? 0.72 : 1)
      .setTint(baitPenLocked ? 0x8d8176 : 0xffffff);
    const bankPulse = 0.5 + Math.sin(this.time.now / 260) * 0.5;
    const bankColor = 0xffd66b;
    this.hazardLayer.fillStyle(bankColor, 0.035 + bankPulse * 0.018);
    this.hazardLayer.fillCircle(AMBER_CACHE_X, AMBER_CACHE_Y, OBJECTIVE_STATION_RADIUS);
    this.hazardLayer.lineStyle(3, bankColor, 0.36 + bankPulse * 0.16);
    this.hazardLayer.strokeCircle(AMBER_CACHE_X, AMBER_CACHE_Y, OBJECTIVE_STATION_RADIUS);
    if (bankProgress > 0) {
      this.hazardLayer.lineStyle(8, 0xffd66b, 0.9);
      this.hazardLayer.beginPath();
      this.hazardLayer.arc(
        AMBER_CACHE_X,
        AMBER_CACHE_Y,
        OBJECTIVE_STATION_RADIUS - 7,
        -Math.PI / 2,
        -Math.PI / 2 + Math.PI * 2 * bankProgress,
        false,
      );
      this.hazardLayer.strokePath();
    }
    const baitColor = baitPenLocked ? 0xa16455 : 0xff765c;
    this.hazardLayer.fillStyle(baitColor, baitPenLocked ? 0.025 : 0.045 + bankPulse * 0.018);
    this.hazardLayer.fillCircle(BAIT_PEN_X, BAIT_PEN_Y, OBJECTIVE_STATION_RADIUS);
    this.hazardLayer.lineStyle(3, baitColor, baitPenLocked ? 0.3 : 0.52 + bankPulse * 0.14);
    this.hazardLayer.strokeCircle(BAIT_PEN_X, BAIT_PEN_Y, OBJECTIVE_STATION_RADIUS);
    if (baitProgress > 0) {
      this.hazardLayer.lineStyle(7, 0xff765c, 0.92);
      this.hazardLayer.beginPath();
      this.hazardLayer.arc(
        BAIT_PEN_X,
        BAIT_PEN_Y,
        OBJECTIVE_STATION_RADIUS - 7,
        -Math.PI / 2,
        -Math.PI / 2 + Math.PI * 2 * baitProgress,
        false,
      );
      this.hazardLayer.strokePath();
    }

    if (this.snapshot.teamCombo) {
      const [x, y, size, progress, occupants] = this.snapshot.teamCombo;
      const pulse = 0.5 + Math.sin(this.time.now / 125) * 0.5;
      const color = occupants >= 2 ? 0xffd66b : 0x69e7ff;
      this.hazardLayer.fillStyle(color, 0.07 + pulse * 0.035);
      this.hazardLayer.fillRect(x - size / 2, y - size / 2, size, size);
      this.hazardLayer.lineStyle(6, color, 0.62 + pulse * 0.28);
      this.hazardLayer.strokeRect(x - size / 2, y - size / 2, size, size);
      this.hazardLayer.fillStyle(color, 0.9);
      this.hazardLayer.fillRect(
        x - size / 2,
        y + size / 2 - 12,
        size * progress,
        12,
      );
      this.hazardLayer.lineStyle(2, 0xffffff, 0.65);
      this.hazardLayer.strokeRect(x - 30, y - 30, 60, 60);
      this.hazardLayer.lineStyle(4, color, 0.8);
      this.hazardLayer.strokeCircle(x, y, 42 + pulse * 6);
      this.hazardLayer.lineBetween(x - 22, y, x + 22, y);
      this.hazardLayer.lineBetween(x, y - 22, x, y + 22);
      for (const cornerX of [-1, 1]) {
        for (const cornerY of [-1, 1]) {
          this.hazardLayer.fillStyle(0xffd66b, 0.72 + pulse * 0.2);
          this.hazardLayer.fillRect(
            x + cornerX * (size / 2 - 15) - 6,
            y + cornerY * (size / 2 - 15) - 6,
            12,
            12,
          );
        }
      }
    }

    if (this.snapshot.triangleRally) {
      const [zones, progress, occupiedZones, requiredZones] =
        this.snapshot.triangleRally;
      const pulse = 0.5 + Math.sin(this.time.now / 150) * 0.5;
      const complete = occupiedZones >= requiredZones && requiredZones > 0;
      for (const [x, y, size, zoneOccupants] of zones) {
        const color = complete
          ? 0xff8fd1
          : zoneOccupants > 0
            ? 0xffd66b
            : 0x69e7ff;
        const height = size * 0.866;
        const topY = y - (height * 2) / 3;
        const bottomY = y + height / 3;
        this.hazardLayer.fillStyle(color, 0.08 + pulse * 0.045);
        this.hazardLayer.fillTriangle(
          x,
          topY,
          x - size / 2,
          bottomY,
          x + size / 2,
          bottomY,
        );
        this.hazardLayer.lineStyle(7, color, 0.65 + pulse * 0.28);
        this.hazardLayer.strokeTriangle(
          x,
          topY,
          x - size / 2,
          bottomY,
          x + size / 2,
          bottomY,
        );
        this.hazardLayer.fillStyle(0x17251b, 0.9);
        this.hazardLayer.fillRoundedRect(x - 22, y - 15, 44, 30, 5);
        this.hazardLayer.lineStyle(4, color, 0.92);
        this.hazardLayer.strokeRoundedRect(x - 22, y - 15, 44, 30, 5);
        this.hazardLayer.fillStyle(
          zoneOccupants > 0 ? 0xffd66b : 0x69e7ff,
          0.75 + pulse * 0.2,
        );
        this.hazardLayer.fillCircle(x, y, 6 + pulse * 2);
        const innerSize = size * (0.2 + progress * 0.68);
        const innerHeight = innerSize * 0.866;
        this.hazardLayer.lineStyle(3, 0xffffff, 0.32 + progress * 0.58);
        this.hazardLayer.strokeTriangle(
          x,
          y - (innerHeight * 2) / 3,
          x - innerSize / 2,
          y + innerHeight / 3,
          x + innerSize / 2,
          y + innerHeight / 3,
        );
      }
    }

    for (const [
      ,
      kind,
      x,
      y,
      radius,
      triggerAt,
      expiresAt,
      startAngle,
      angularSpeed,
      width,
      directionX,
      directionY,
      gapCenter,
      gapSize,
    ] of this.snapshot.hazards) {
      if (kind === "army") {
        const active = now >= triggerAt;
        const travelSeconds = Math.max(0, now - triggerAt) / 1_000;
        const vertical = directionX !== 0;
        const rawWallPosition = vertical
          ? x + directionX * angularSpeed * travelSeconds
          : y + directionY * angularSpeed * travelSeconds;
        const direction = vertical ? directionX : directionY;
        const axisMinimum = vertical ? arenaX : arenaY;
        const axisMaximum = vertical
          ? arenaX + arenaWidth
          : arenaY + arenaHeight;
        const beforeEntry = direction > 0
          ? rawWallPosition < axisMinimum
          : rawWallPosition > axisMaximum;
        const afterExit = direction > 0
          ? rawWallPosition > axisMaximum
          : rawWallPosition < axisMinimum;
        if (afterExit) continue;
        const wallPosition = beforeEntry
          ? direction > 0
            ? axisMinimum
            : axisMaximum
          : rawWallPosition;
        const pulse = 0.5 + Math.sin(this.time.now / 80) * 0.5;
        const gapStart = gapCenter - gapSize / 2;
        const gapEnd = gapCenter + gapSize / 2;
        const axisStart = vertical ? arenaY : arenaX;
        const axisEnd = vertical
          ? arenaY + arenaHeight
          : arenaX + arenaWidth;
        const lineColor = active ? 0xff385e : 0xffa052;
        this.hazardLayer.lineStyle(
          active ? width : 14,
          lineColor,
          active ? 0.38 + pulse * 0.18 : 0.42,
        );
        if (vertical) {
          this.hazardLayer.lineBetween(
            wallPosition,
            arenaY,
            wallPosition,
            gapStart,
          );
          this.hazardLayer.lineBetween(
            wallPosition,
            gapEnd,
            wallPosition,
            arenaY + arenaHeight,
          );
        } else {
          this.hazardLayer.lineBetween(
            arenaX,
            wallPosition,
            gapStart,
            wallPosition,
          );
          this.hazardLayer.lineBetween(
            gapEnd,
            wallPosition,
            arenaX + arenaWidth,
            wallPosition,
          );
        }
        for (
          let position = axisStart + 18;
          position < axisEnd;
          position += 36
        ) {
          if (position >= gapStart && position <= gapEnd) continue;
          const jitter = Math.sin(position * 13.7) * 9;
          const gallop = Math.sin(this.time.now / 130 + position) * 3;
          const dotX = vertical ? wallPosition + jitter : position + gallop;
          const dotY = vertical ? position + gallop : wallPosition + jitter;
          this.hazardLayer.fillStyle(0x14231b, active ? 0.3 : 0.16);
          this.hazardLayer.fillCircle(dotX, dotY + 10, 8);
          let herd = this.stampedeSprites[stampedeUsed];
          if (!herd) {
            herd = this.add.sprite(dotX, dotY, "galli", 0).setDepth(5.5);
            this.stampedeSprites.push(herd);
          }
          stampedeUsed++;
          const herdAnim = vertical
            ? directionX > 0
              ? "galli-right"
              : "galli-left"
            : position % 72 < 36
              ? "galli-left"
              : "galli-right";
          herd.play(herdAnim, true);
          herd
            .setVisible(true)
            .setPosition(dotX, dotY)
            .setScale((active ? 68 : 56) / herd.width)
            .setAlpha(active ? 1 : 0.75);
        }
        this.hazardLayer.lineStyle(7, 0x8dffcf, 0.82 + pulse * 0.16);
        if (vertical) {
          this.hazardLayer.lineBetween(
            wallPosition - 18,
            gapStart,
            wallPosition + 18,
            gapStart,
          );
          this.hazardLayer.lineBetween(
            wallPosition - 18,
            gapEnd,
            wallPosition + 18,
            gapEnd,
          );
        } else {
          this.hazardLayer.lineBetween(
            gapStart,
            wallPosition - 18,
            gapStart,
            wallPosition + 18,
          );
          this.hazardLayer.lineBetween(
            gapEnd,
            wallPosition - 18,
            gapEnd,
            wallPosition + 18,
          );
        }
        continue;
      }
      if (kind === "laser") {
        const active = now >= triggerAt;
        const angle =
          startAngle + angularSpeed * (Math.max(0, now - triggerAt) / 1_000);
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const perpendicularX = -sin;
        const perpendicularY = cos;
        const pulse = 0.5 + Math.sin(this.time.now / 65) * 0.5;
        const segments = 24;
        const drawCable = (lineWidth: number, color: number, alpha: number, jitter: number) => {
          this.hazardLayer.lineStyle(lineWidth, color, alpha);
          this.hazardLayer.beginPath();
          this.hazardLayer.moveTo(x, y);
          for (let segment = 1; segment <= segments; segment += 1) {
            const progress = segment / segments;
            const distanceAlong = radius * progress;
            const taper = Math.sin(progress * Math.PI);
            const wobble =
              Math.sin(segment * 1.73 + this.time.now / 145) * jitter * taper;
            this.hazardLayer.lineTo(
              x + cos * distanceAlong + perpendicularX * wobble,
              y + sin * distanceAlong + perpendicularY * wobble,
            );
          }
          this.hazardLayer.strokePath();
        };
        drawCable(active ? 11 : 8, 0x222820, active ? 0.92 : 0.68, 8);
        drawCable(active ? 3 : 2, active ? 0xbdfcff : 0xff8d62, active ? 0.9 : 0.65, 13);
        for (const progress of [0.22, 0.5, 0.78]) {
          const distanceAlong = radius * progress;
          const wobble =
            Math.sin(progress * segments * 1.73 + this.time.now / 145) *
            8 *
            Math.sin(progress * Math.PI);
          const cableX = x + cos * distanceAlong + perpendicularX * wobble;
          const cableY = y + sin * distanceAlong + perpendicularY * wobble;
          let cable = this.cableSprites[cableUsed];
          if (!cable) {
            cable = this.add.image(cableX, cableY, "effect-broken-cable").setDepth(4.9);
            this.cableSprites.push(cable);
          }
          cableUsed++;
          cable
            .setVisible(true)
            .setPosition(cableX, cableY)
            .setRotation(angle)
            .setScale(active ? 1 : 0.86)
            .setAlpha(active ? 0.95 : 0.72);
        }
        for (let segment = 3; segment < segments; segment += 4) {
          const progress = segment / segments;
          const distanceAlong = radius * progress;
          const spark = Math.sin(segment * 2.1 + this.time.now / 95) * 12;
          const sparkX = x + cos * distanceAlong + perpendicularX * spark;
          const sparkY = y + sin * distanceAlong + perpendicularY * spark;
          this.hazardLayer.fillStyle(active ? 0xeaffff : 0xffb071, 0.55 + pulse * 0.35);
          this.hazardLayer.fillCircle(sparkX, sparkY, active ? 3.5 : 2.5);
          if (segment === 7 || segment === 15) {
            let sparkSprite = this.sparkSprites[sparkUsed];
            if (!sparkSprite) {
              sparkSprite = this.add
                .sprite(sparkX, sparkY, "effect-electric-spark-sequence", 0)
                .setDepth(5.1);
              this.sparkSprites.push(sparkSprite);
            }
            sparkUsed++;
            sparkSprite
              .setVisible(true)
              .setFrame(
                Math.floor(this.time.now / 75 + segment * 0.5) % 4,
              )
              .setPosition(sparkX, sparkY)
              .setRotation(this.time.now / 180 + segment)
              .setScale((active ? 0.88 : 0.64) + pulse * 0.18)
              .setAlpha(active ? 0.9 : 0.62);
          }
        }
        this.hazardLayer.lineStyle(4, 0xbdfcff, active ? 0.8 : 0.45);
        this.hazardLayer.strokeCircle(x, y, active ? 18 : 13);
        continue;
      }
      if (kind === "collapse") {
        const active = now >= triggerAt;
        const warningWindow = Math.max(1, triggerAt - (expiresAt - 3_850));
        const remainingRatio = Phaser.Math.Clamp(
          (triggerAt - now) / Math.max(1_100, warningWindow),
          0,
          1,
        );
        const shownRadius = active
          ? radius
          : radius + (width - radius) * remainingRatio;
        const pulse = 0.5 + Math.sin(this.time.now / 75) * 0.5;
        this.hazardLayer.fillStyle(0xb51f48, active ? 0.3 : 0.08 + pulse * 0.035);
        this.hazardLayer.fillRect(arenaX, arenaY, arenaWidth, arenaHeight);
        this.hazardLayer.fillStyle(0xffd66b, active ? 0.25 : 0.1);
        this.hazardLayer.fillCircle(x, y, shownRadius);
        this.hazardLayer.lineStyle(
          active ? 12 : 8,
          active ? 0xffffff : 0xffd66b,
          0.82 + pulse * 0.16,
        );
        this.hazardLayer.strokeCircle(x, y, shownRadius);
        this.hazardLayer.lineStyle(3, 0xff9a52, 0.92);
        this.hazardLayer.strokeCircle(x, y, radius);
        this.hazardLayer.fillStyle(0xff4f32, 0.18 + pulse * 0.16);
        this.hazardLayer.fillCircle(x, y, 23 + pulse * 8);
        this.rallyFlareSprite
          .setVisible(true)
          .setPosition(x, y - 18)
          .setScale((active ? 1.04 : 0.94) + pulse * 0.06)
          .setAlpha(active ? 1 : 0.84);
        this.rallySmokeSprite
          .setVisible(true)
          .setFrame(Math.floor(this.time.now / 145) % 4)
          .setPosition(x, y - 74 - pulse * 5)
          .setScale(0.84 + pulse * 0.1)
          .setAlpha(active ? 0.78 : 0.58);
        continue;
      }
      if (kind === "hatch") {
        const matriarchHatch = radius >= 100;
        const crackPhase = matriarchHatch
          ? Phaser.Math.Clamp((now - (triggerAt - 850)) / 850, 0, 1)
          : 1;
        const aftermathPhase = Phaser.Math.Clamp(
          (now - triggerAt) / Math.max(1, expiresAt - triggerAt),
          0,
          1,
        );
        const phase =
          now < triggerAt
            ? crackPhase * 0.75
            : 0.75 + aftermathPhase * 0.25;
        const hatchFrame =
          now < triggerAt
            ? Math.min(2, Math.floor(crackPhase * 3))
            : 3;
        const hatchPulse = Math.sin(this.time.now / 85 + x * 0.01) * 0.06;
        let hatch = this.hatchSprites[hatchUsed];
        if (!hatch) {
          hatch = this.add
            .sprite(x, y, "effect-egg-sequence", 0)
            .setDepth(5.4);
          this.hatchSprites.push(hatch);
        }
        hatchUsed++;
        hatch
          .setVisible(true)
          .setFrame(hatchFrame)
          .setPosition(x, y)
          .setScale(1.08 + phase * 0.18 + hatchPulse)
          .setRotation(Math.sin(this.time.now / 115 + y * 0.01) * (1 - crackPhase) * 0.06)
          .setAlpha(1 - aftermathPhase * 0.48);
        this.hazardLayer.lineStyle(4, 0xffefc4, 0.8 * (1 - aftermathPhase));
        this.hazardLayer.strokeCircle(x, y, radius * (0.25 + phase * 0.75));
        for (let fragment = 0; fragment < 10; fragment += 1) {
          const angle = (fragment / 10) * Math.PI * 2;
          const distance = radius * (0.2 + phase * 0.65);
          this.hazardLayer.fillStyle(0xffe9b0, 0.86 * (1 - aftermathPhase));
          this.hazardLayer.fillCircle(
            x + Math.cos(angle) * distance,
            y + Math.sin(angle) * distance,
            5,
          );
        }
        continue;
      }
      if (kind === "bog") {
        let bog = this.bogSprites[bogUsed];
        if (!bog) {
          bog = this.add.image(x, y, "effect-bog-puddle").setDepth(2.92);
          this.bogSprites.push(bog);
        }
        bogUsed++;
        bog
          .setVisible(true)
          .setPosition(x, y)
          .setRotation((x + y) * 0.001)
          .setScale((radius * 2) / 96)
          .setAlpha(now >= triggerAt ? 0.82 : 0.58);
      }
      if (kind === "stomp") {
        let cracks = this.stompCrackSprites[stompUsed];
        if (!cracks) {
          cracks = this.add
            .image(x, y, "effect-stomp-cracks")
            .setDepth(3.05);
          this.stompCrackSprites.push(cracks);
        }
        stompUsed++;
        cracks
          .setVisible(true)
          .setPosition(x, y)
          .setScale((radius * 2) / 96)
          .setAlpha(now >= triggerAt ? 0.9 : 0.55);
        if (now >= triggerAt) {
          const impactPhase = Phaser.Math.Clamp(
            (now - triggerAt) / Math.max(1, expiresAt - triggerAt),
            0,
            1,
          );
          let dust = this.dustBurstSprites[dustUsed];
          if (!dust) {
            dust = this.add.image(x, y, "effect-dust-burst").setDepth(5.25);
            this.dustBurstSprites.push(dust);
          }
          dustUsed++;
          dust
            .setVisible(true)
            .setPosition(x, y - radius * 0.08)
            .setScale(((radius * 1.5) / 64) * (0.75 + impactPhase * 0.7))
            .setAlpha(1 - impactPhase);
        }
      }
      if (now < triggerAt) {
        const remaining = Math.max(0, (triggerAt - now) / 1_650);
        const color =
          kind === "pulse" ? 0xca63ff : kind === "blight" ? 0x6c9228 : 0xff586d;
        if (kind === "blight") {
          this.hazardLayer.fillStyle(0x10180a, 0.72);
          this.hazardLayer.fillCircle(x, y, radius);
          this.hazardLayer.fillStyle(0x456516, 0.48);
          this.hazardLayer.fillCircle(x - radius * 0.2, y - radius * 0.2, radius * 0.6);
          this.hazardLayer.lineStyle(5, 0xa7d84e, 0.92);
          this.hazardLayer.strokeCircle(x, y, radius * (0.75 + remaining * 0.25));
        } else if (kind === "bog") {
          this.hazardLayer.fillStyle(0x211c0f, 0.16);
          this.hazardLayer.fillCircle(x, y, radius);
          this.hazardLayer.fillStyle(0x4a3b1c, 0.12);
          this.hazardLayer.fillCircle(x - radius * 0.18, y + radius * 0.08, radius * 0.72);
          this.hazardLayer.fillStyle(0x657437, 0.18);
          this.hazardLayer.fillCircle(x + radius * 0.42, y - radius * 0.25, radius * 0.28);
          this.hazardLayer.lineStyle(5, 0x8e9d4f, 0.88);
          this.hazardLayer.strokeCircle(x, y, radius * (0.74 + remaining * 0.26));
          for (let bubble = 0; bubble < 7; bubble += 1) {
            const angle = bubble * 2.39 + this.time.now / 700;
            const bubbleRadius = radius * (0.25 + (bubble % 3) * 0.18);
            this.hazardLayer.lineStyle(2, 0xb1b56c, 0.38);
            this.hazardLayer.strokeCircle(
              x + Math.cos(angle) * bubbleRadius,
              y + Math.sin(angle) * bubbleRadius,
              3 + (bubble % 3) * 2,
            );
          }
        } else if (kind === "stomp") {
          this.hazardLayer.fillStyle(0xa8753c, 0.16 + (1 - remaining) * 0.12);
          this.hazardLayer.fillCircle(x, y, radius);
          this.hazardLayer.lineStyle(7, 0xd4a35f, 0.84);
          this.hazardLayer.strokeCircle(x, y, radius * (0.68 + remaining * 0.32));
          for (let crack = 0; crack < 10; crack += 1) {
            const angle = (crack / 10) * Math.PI * 2;
            this.hazardLayer.lineStyle(3, 0x5b3b21, 0.58);
            this.hazardLayer.lineBetween(
              x + Math.cos(angle) * radius * 0.18,
              y + Math.sin(angle) * radius * 0.18,
              x + Math.cos(angle) * radius * (0.58 + (crack % 2) * 0.18),
              y + Math.sin(angle) * radius * (0.58 + (crack % 2) * 0.18),
            );
          }
        } else if (kind === "screech") {
          const siren = [...this.enemies.values()]
            .filter((enemy) => enemy.kind === "nullbell")
            .sort(
              (a, b) =>
                Phaser.Math.Distance.Between(a.body.x, a.body.y, x, y) -
                Phaser.Math.Distance.Between(b.body.x, b.body.y, x, y),
            )[0];
          const beaconOn = Math.floor(this.time.now / 110) % 2 === 0;
          if (siren?.sprite) {
            siren.sprite.setTint(beaconOn ? 0xff6464 : 0xffffff);
            this.hazardLayer.fillStyle(
              beaconOn ? 0xff4f4f : 0xfff0b0,
              0.72,
            );
            this.hazardLayer.fillCircle(
              siren.body.x,
              siren.body.y - siren.size * 0.72,
              beaconOn ? 7 : 4,
            );
          }
          for (let ring = 1; ring <= 3; ring += 1) {
            this.hazardLayer.lineStyle(4, 0xdaf8ff, 0.35 + ring * 0.13);
            this.hazardLayer.strokeCircle(
              x,
              y,
              radius * Phaser.Math.Clamp(1 - remaining + ring * 0.2, 0.2, 1),
            );
          }
        } else {
          this.hazardLayer.fillStyle(color, 0.34 + (1 - remaining) * 0.2);
          this.hazardLayer.fillCircle(x, y, radius);
          this.hazardLayer.lineStyle(
            5,
            kind === "pulse" ? 0xe2a2ff : 0xff8796,
            0.85,
          );
          this.hazardLayer.strokeCircle(x, y, radius * (0.7 + remaining * 0.3));
        }
      } else if (now < expiresAt) {
        const activeColor =
          kind === "blight"
            ? 0x789c31
            : kind === "bog"
              ? 0x4a3b1c
              : kind === "stomp"
                ? 0xd4a35f
                : kind === "screech"
                  ? 0xdaf8ff
                  : 0xffffff;
        const activeAlpha =
          kind === "screech"
            ? 0.18
            : kind === "bog" || kind === "stomp"
              ? 0.14
              : 0.65;
        this.hazardLayer.fillStyle(activeColor, activeAlpha);
        this.hazardLayer.fillCircle(x, y, radius);
      }
    }
  }

  startLocalDash(): void {
    const now = performance.now();
    const length = Math.hypot(this.inputX, this.inputY);
    if (now < this.localDashReadyAt || length < 0.1) return;
    this.localDashX = this.inputX / length;
    this.localDashY = this.inputY / length;
    this.localDashUntil = now + 180;
    const serverNow = Date.now() + connection.serverOffset;
    this.localDashReadyAt =
      now +
      (playerState && serverNow < playerState.energyBarUntil ? 850 : 1_500);
    connection.send({ t: "dash", seq: ++this.inputSeq });
  }

  private updateTouchDashHud(time: number): void {
    if (!touchInput) return;
    const remaining = Math.max(0, this.localDashReadyAt - time);
    const ready = remaining === 0 && !playerState?.ghost;
    dom.touchDash.disabled = !ready;
    dom.touchDash.classList.toggle("ready", ready);
    dom.touchDashCooldown.textContent = ready
      ? "READY"
      : playerState?.ghost
        ? "DOWN"
        : (remaining / 1_000).toFixed(1) + "s";
  }

  fireLootBomb(centerOnPlayer = false): void {
    if (!playerState || playerState.ghost) return;
    const serverNow = Date.now() + connection.serverOffset;
    if (serverNow < playerState.specialReadyAt) return;
    const target = this.lootBombTarget(centerOnPlayer);
    if (playerState.ultimateCharges > 0) {
      const barrage = consumeUltimateBarrageCharge(
        playerState.ultimateCharges,
        serverNow,
        ULTIMATE_COOLDOWNS[playerState.ability],
      );
      playerState.ultimateCharges = barrage.remaining;
      playerState.specialReadyAt = barrage.readyAt;
    } else {
      playerState.specialReadyAt =
        serverNow + ULTIMATE_COOLDOWNS[playerState.ability];
    }
    connection.send({
      t: "special",
      x: Math.round(target.x),
      y: Math.round(target.y),
      seq: ++this.inputSeq,
    });
  }

  private lootBombTarget(centerOnPlayer = false): Phaser.Math.Vector2 {
    const player = this.players.get(connection.localId);
    if (
      player &&
      playerState &&
      ["moon", "frost", "necro", "bard"].includes(playerState.ability)
    ) {
      return new Phaser.Math.Vector2(player.body.x, player.body.y);
    }
    const pointer = this.input.activePointer;
    const target = new Phaser.Math.Vector2(pointer.worldX, pointer.worldY);
    if (!player) return target;
    const point = specialTargetPoint(
      { x: player.body.x, y: player.body.y },
      { x: target.x, y: target.y },
      centerOnPlayer,
    );
    return new Phaser.Math.Vector2(point.x, point.y);
  }

  private drawCombatEffects(time: number): void {
    this.effectLayer.clear();
    let amberUsed = 0;
    let venomProjectileUsed = 0;
    let venomSplatterUsed = 0;
    let tranqRuptureUsed = 0;
    for (const shard of this.amberSprites) shard.setVisible(false);
    for (const projectile of this.venomProjectileSprites) projectile.setVisible(false);
    for (const splatter of this.venomSplatterSprites) splatter.setVisible(false);
    for (const rupture of this.tranqRuptureSprites) rupture.setVisible(false);
    const serverNow = Date.now() + connection.serverOffset;

    for (const [id, effect] of this.combatFx) {
      const [, kind, attackerId, x, y, radius, targets, createdAt] = effect;
      const duration =
        kind === "raid"
          ? 1_300
          : kind === "venom"
            ? 520
          : kind === "special"
            ? 700
            : kind === "reaction" || kind === "necro"
              ? 620
              : 340;
      const age = serverNow - createdAt;
      if (age > duration) {
        this.combatFx.delete(id);
        continue;
      }
      const phase = Phaser.Math.Clamp(age / duration, 0, 1);
      const color =
        kind === "venom"
          ? 0x8fd05c
          : kind === "raid"
          ? 0xffe16b
          : kind === "reaction"
            ? 0x8dfff2
        : kind === "special"
          ? ABILITY_COLORS[this.players.get(attackerId)?.ability ?? "solar"]
          : kind === "mote"
            ? 0x62e8c2
            : ABILITY_COLORS[kind];
      const alpha = 0.9 * (1 - phase);

      if (kind === "venom") {
        const [targetX, targetY] = targets[0] ?? [x, y];
        const travelPhase = Phaser.Math.Clamp(phase / 0.65, 0, 1);
        const projectileX = Phaser.Math.Linear(x, targetX, travelPhase);
        const projectileY =
          Phaser.Math.Linear(y, targetY, travelPhase) -
          Math.sin(travelPhase * Math.PI) * 22;
        let projectile = this.venomProjectileSprites[venomProjectileUsed];
        if (!projectile) {
          projectile = this.add
            .image(projectileX, projectileY, "effect-venom-projectile")
            .setDepth(7.3);
          this.venomProjectileSprites.push(projectile);
        }
        venomProjectileUsed++;
        projectile
          .setVisible(phase < 0.72)
          .setPosition(projectileX, projectileY)
          .setRotation(Math.atan2(targetY - y, targetX - x))
          .setScale(0.9 + travelPhase * 0.14)
          .setAlpha(phase < 0.65 ? 1 : Math.max(0, 1 - (phase - 0.65) * 14));
        if (phase >= 0.48) {
          let splatter = this.venomSplatterSprites[venomSplatterUsed];
          if (!splatter) {
            splatter = this.add
              .image(targetX, targetY, "effect-venom-splatter")
              .setDepth(7.2);
            this.venomSplatterSprites.push(splatter);
          }
          venomSplatterUsed++;
          splatter
            .setVisible(true)
            .setPosition(targetX, targetY)
            .setRotation(id * 0.61)
            .setScale(0.4 + (phase - 0.48) * 1.25)
            .setAlpha(1 - phase);
        }
      } else if (kind === "raid") {
        this.effectLayer.lineStyle(2, 0xfff0a8, alpha * 0.78);
        targets.forEach(([targetX, targetY], index) => {
          const side = index % 4;
          const fromX =
            side === 0
              ? this.activeArena[0]
              : side === 1
                ? this.activeArena[0] + this.activeArena[2]
                : targetX + ((index % 7) - 3) * 34;
          const fromY =
            side === 2
              ? this.activeArena[1]
              : side === 3
                ? this.activeArena[1] + this.activeArena[3]
                : targetY + ((index % 5) - 2) * 28;
          this.effectLayer.lineBetween(fromX, fromY, targetX, targetY);
          this.effectLayer.fillStyle(0xffd66b, alpha * 0.9);
          this.effectLayer.fillCircle(targetX, targetY, 5 + (1 - phase) * 9);
        });
        this.effectLayer.lineStyle(8, 0xffd66b, alpha * 0.55);
        this.effectLayer.strokeCircle(x, y, radius * (0.2 + phase * 0.8));
      } else if (kind === "ember" || kind === "raven") {
        this.effectLayer.lineStyle(kind === "raven" ? 3 : 6, color, alpha);
        for (const [targetX, targetY] of targets) {
          this.effectLayer.lineBetween(x, y, targetX, targetY);
          this.effectLayer.fillStyle(color, alpha);
          this.effectLayer.fillCircle(targetX, targetY, 7 + phase * 10);
        }
      } else if (kind === "chain") {
        this.effectLayer.lineStyle(4, color, alpha);
        let fromX = x;
        let fromY = y;
        for (const [targetX, targetY] of targets) {
          this.effectLayer.lineBetween(fromX, fromY, targetX, targetY);
          fromX = targetX;
          fromY = targetY;
        }
      } else if (kind === "necro") {
        if (targets.length) {
          this.effectLayer.lineStyle(4, color, alpha);
          for (const [targetX, targetY] of targets) {
            this.effectLayer.lineBetween(x, y, targetX, targetY);
            this.effectLayer.fillStyle(color, alpha * 0.7);
            this.effectLayer.fillCircle(targetX, targetY, 5 + (1 - phase) * 5);
          }
        } else {
          const shownRadius = radius * (0.25 + phase * 0.75);
          this.effectLayer.fillStyle(color, alpha * 0.12);
          this.effectLayer.fillCircle(x, y, shownRadius);
          this.effectLayer.lineStyle(5, color, alpha);
          this.effectLayer.strokeCircle(x, y, shownRadius);
        }
      } else {
        const shownRadius =
          radius *
          (kind === "special" ? 0.18 + phase * 0.82 : 1);
        this.effectLayer.fillStyle(
          color,
          kind === "special" ? alpha * 0.18 : alpha * 0.12,
        );
        this.effectLayer.fillCircle(x, y, shownRadius);
        this.effectLayer.lineStyle(
          kind === "special" ? 8 : 5,
          color,
          alpha,
        );
        this.effectLayer.strokeCircle(x, y, shownRadius);
        for (const [targetX, targetY] of targets) {
          this.effectLayer.fillStyle(color, alpha * 0.9);
          this.effectLayer.fillCircle(targetX, targetY, 5 + (1 - phase) * 7);
        }
      }
    }

    if (this.snapshot) {
      for (const [, x, y, amount, expiresAt] of this.snapshot.healingDrops) {
        const pulse = 0.5 + Math.sin(time / 180 + x) * 0.5;
        const expiryAlpha = Phaser.Math.Clamp((expiresAt - serverNow) / 2_000, 0.2, 1);
        this.effectLayer.fillStyle(0x58e88c, (0.14 + pulse * 0.12) * expiryAlpha);
        this.effectLayer.fillCircle(x, y, 17 + pulse * 4);
        this.effectLayer.fillStyle(0x8dffae, expiryAlpha);
        this.effectLayer.fillRoundedRect(x - 4, y - 11, 8, 22, 3);
        this.effectLayer.fillRoundedRect(x - 11, y - 4, 22, 8, 3);
        if (amount >= 40) {
          this.effectLayer.lineStyle(2, 0xd4ffe0, expiryAlpha);
          this.effectLayer.strokeCircle(x, y, 20 + pulse * 3);
        }
      }

      for (const [, x, y, expiresAt] of this.snapshot.motes) {
        const pulse = 0.5 + Math.sin(time / 130 + x * 0.03) * 0.5;
        const expiryAlpha = Phaser.Math.Clamp((expiresAt - serverNow) / 1_500, 0.18, 1);
        this.effectLayer.fillStyle(0xf9c80e, (0.1 + pulse * 0.08) * expiryAlpha);
        this.effectLayer.fillCircle(x, y, 11 + pulse * 3);
        let shard = this.amberSprites[amberUsed];
        if (!shard) {
          shard = this.add.image(x, y, "amber").setDepth(4.5);
          this.amberSprites.push(shard);
        }
        amberUsed++;
        shard
          .setVisible(true)
          .setPosition(x, y - 2 - pulse * 2)
          .setScale(0.8 + pulse * 0.1)
          .setAlpha(expiryAlpha);
      }

      if (this.snapshot.overdriveDrop) {
        const [, x, y, expiresAt] = this.snapshot.overdriveDrop;
        const pulse = 0.5 + Math.sin(time / 105) * 0.5;
        const expiryAlpha = Phaser.Math.Clamp((expiresAt - serverNow) / 1_500, 0.2, 1);
        this.effectLayer.fillStyle(0xff754f, (0.12 + pulse * 0.1) * expiryAlpha);
        this.effectLayer.fillCircle(x, y, 29 + pulse * 8);
        this.effectLayer.lineStyle(5, 0xffd66b, expiryAlpha);
        this.effectLayer.strokeCircle(x, y, 20 + pulse * 6);
        this.effectLayer.fillStyle(0xfff1a8, expiryAlpha);
        this.effectLayer.fillCircle(x, y, 9);
      }

      if (serverNow < this.snapshot.overdriveUntil) {
        const pulse = 0.5 + Math.sin(time / 72) * 0.5;
        this.effectLayer.lineStyle(3, 0xff513f, 0.5 + pulse * 0.28);
        for (const [id, enemy] of this.enemies) {
          const twitch = Math.sin(time / 34 + id * 1.7) * (3 + pulse * 3);
          this.effectLayer.strokeCircle(
            enemy.body.x + twitch,
            enemy.body.y - twitch * 0.4,
            enemy.size + 7 + pulse * 5,
          );
          this.effectLayer.lineBetween(
            enemy.body.x - 12 - twitch,
            enemy.body.y + enemy.size * 0.35,
            enemy.body.x - 2,
            enemy.body.y + enemy.size * 0.1,
          );
          enemy.body
            .setScale(1 + pulse * 0.1)
            .setAlpha(enemy.baseAlpha * (0.84 + pulse * 0.16));
          enemy.sprite?.setAlpha(0.84 + pulse * 0.16);
        }
        this.effectLayer.lineStyle(3, 0xffd66b, 0.5 + pulse * 0.24);
        for (const player of this.players.values()) {
          if (!player.ghost) {
            this.effectLayer.strokeCircle(player.body.x, player.body.y, 27 + pulse * 3);
          }
        }
      } else {
        for (const enemy of this.enemies.values()) {
          enemy.body.setScale(1).setAlpha(enemy.baseAlpha);
          enemy.sprite?.setAlpha(1);
        }
      }
    }

    if (currentLoot && currentLoot.source !== "grave") {
      const localPlayer = this.players.get(connection.localId);
      if (localPlayer && !localPlayer.ghost) {
        const pulse = 0.5 + Math.sin(time / 115) * 0.5;
        this.effectLayer.fillStyle(0x69e7ff, 0.08 + pulse * 0.08);
        this.effectLayer.fillCircle(
          localPlayer.body.x,
          localPlayer.body.y,
          31 + pulse * 5,
        );
        this.effectLayer.lineStyle(4, 0x9ff4ff, 0.7 + pulse * 0.25);
        this.effectLayer.strokeCircle(
          localPlayer.body.x,
          localPlayer.body.y,
          27 + pulse * 4,
        );
      }
    }

    if (arenaTouch.targetX !== null && arenaTouch.targetY !== null) {
      const pulse = 0.5 + Math.sin(time / 150) * 0.5;
      this.effectLayer.fillStyle(0x9fe8ff, 0.08 + pulse * 0.05);
      this.effectLayer.fillCircle(arenaTouch.targetX, arenaTouch.targetY, 18 + pulse * 4);
      this.effectLayer.lineStyle(3, 0x9fe8ff, 0.58 + pulse * 0.24);
      this.effectLayer.strokeCircle(arenaTouch.targetX, arenaTouch.targetY, 13 + pulse * 4);
    }

    const localPlayerView = this.players.get(connection.localId);
    if (
      localPlayerView &&
      !localPlayerView.ghost &&
      playerState &&
      serverNow < playerState.icePopUntil
    ) {
      const pulse = 0.5 + Math.sin(time / 190) * 0.5;
      this.effectLayer.fillStyle(0x9fe8ff, 0.045 + pulse * 0.025);
      this.effectLayer.fillCircle(localPlayerView.body.x, localPlayerView.body.y, 145);
      this.effectLayer.lineStyle(2, 0xc8f5ff, 0.3 + pulse * 0.14);
      this.effectLayer.strokeCircle(localPlayerView.body.x, localPlayerView.body.y, 145);
    }

    for (const view of this.players.values()) {
      if (!view.ghost && view.ability === "frost") {
        const auraRadius = 190 * view.range;
        const pulse = 0.5 + Math.sin(time / 240 + view.body.x) * 0.5;
        this.effectLayer.fillStyle(0x78cfff, 0.035 + pulse * 0.025);
        this.effectLayer.fillCircle(view.body.x, view.body.y, auraRadius);
        this.effectLayer.lineStyle(2, 0x78cfff, 0.2 + pulse * 0.12);
        this.effectLayer.strokeCircle(view.body.x, view.body.y, auraRadius);
      }
      if (!view.ghost && serverNow < view.inspiredUntil) {
        const pulse = 0.5 + Math.sin(time / 95 + view.body.x * 0.01) * 0.5;
        this.effectLayer.lineStyle(3, 0xff8fcb, 0.45 + pulse * 0.35);
        this.effectLayer.strokeCircle(view.body.x, view.body.y, 27 + pulse * 5);
        this.effectLayer.lineStyle(2, 0xffd66b, 0.35 + pulse * 0.25);
        this.effectLayer.strokeCircle(view.body.x, view.body.y, 33 + pulse * 4);
      }
      if (!view.ghost && serverNow < view.reviveShieldUntil) {
        const remaining = Phaser.Math.Clamp(
          (view.reviveShieldUntil - serverNow) / REVIVE_SHIELD_DURATION_MS,
          0,
          1,
        );
        const pulse = 0.5 + Math.sin(time / 80) * 0.5;
        this.effectLayer.fillStyle(0x9fe8ff, 0.07 + pulse * 0.05);
        this.effectLayer.fillCircle(view.body.x, view.body.y, 31 + pulse * 3);
        this.effectLayer.lineStyle(3, 0x9fe8ff, 0.55 + pulse * 0.3);
        this.effectLayer.strokeCircle(view.body.x, view.body.y, 31 + pulse * 3);
        this.effectLayer.lineStyle(6, 0xd8f7ff, 0.95);
        this.effectLayer.beginPath();
        this.effectLayer.arc(
          view.body.x,
          view.body.y,
          37,
          -Math.PI / 2,
          -Math.PI / 2 + Math.PI * 2 * remaining,
          false,
        );
        this.effectLayer.strokePath();
      }
      if (!view.ghost) continue;
      this.effectLayer.lineStyle(2, 0x9fe8ff, 0.32);
      this.effectLayer.strokeCircle(view.body.x, view.body.y, REVIVE_RADIUS);
      if (view.reviveProgress <= 0) continue;
      this.effectLayer.lineStyle(7, 0x69e7ff, 0.95);
      this.effectLayer.beginPath();
      this.effectLayer.arc(
        view.body.x,
        view.body.y,
        REVIVE_RADIUS,
        -Math.PI / 2,
        -Math.PI / 2 + Math.PI * 2 * view.reviveProgress,
        false,
      );
      this.effectLayer.strokePath();
    }

    this.deathBursts = this.deathBursts.filter((burst) => {
      const phase = (time - burst.createdAt) / 420;
      if (phase >= 1) return false;
      const alpha = 0.8 * (1 - phase);
      if (burst.kind === "weakpoint") {
        let rupture = this.tranqRuptureSprites[tranqRuptureUsed];
        if (!rupture) {
          rupture = this.add
            .sprite(
              burst.x,
              burst.y,
              "effect-tranq-rupture-sequence",
              0,
            )
            .setDepth(7.4);
          this.tranqRuptureSprites.push(rupture);
        }
        tranqRuptureUsed++;
        rupture
          .setVisible(true)
          .setFrame(Math.min(3, Math.floor(phase * 4)))
          .setPosition(burst.x, burst.y)
          .setScale(0.95 + phase * 0.12)
          .setRotation(0)
          .setAlpha(1 - phase);
      }
      this.effectLayer.lineStyle(3, burst.color, alpha);
      this.effectLayer.strokeCircle(burst.x, burst.y, 8 + phase * 26);
      this.effectLayer.fillStyle(burst.color, alpha);
      for (let index = 0; index < 4; index += 1) {
        const angle = index * (Math.PI / 2) + burst.x * 0.01;
        const distance = 8 + phase * 30;
        this.effectLayer.fillCircle(
          burst.x + Math.cos(angle) * distance,
          burst.y + Math.sin(angle) * distance,
          3,
        );
      }
      return true;
    });

    if (playerState && !playerState.ghost && !touchInput) {
      const target = this.lootBombTarget();
      const ready = serverNow >= playerState.specialReadyAt;
      this.effectLayer.lineStyle(2, ready ? 0xffd66b : 0x8b819a, ready ? 0.82 : 0.3);
      this.effectLayer.strokeCircle(target.x, target.y, 165);
      this.effectLayer.lineBetween(target.x - 9, target.y, target.x + 9, target.y);
      this.effectLayer.lineBetween(target.x, target.y - 9, target.x, target.y + 9);
    }
  }
}

const game = new Phaser.Game({
  type: Phaser.AUTO,
  parent: "game-root",
  width: window.innerWidth,
  height: window.innerHeight,
  transparent: true,
  antialias: false,
  render: {
    pixelArt: true,
    roundPixels: true,
  },
  scale: {
    mode: Phaser.Scale.RESIZE,
  },
  scene: [VaultScene],
});

let currentLoot: LootMessage | null = null;
let lootCountdown: number | null = null;
let playerState: PlayerStateMessage | null = null;
let latestSnapshot: SnapshotMessage | null = null;
let classPanelOpen = false;
let lastStage: MatchStage | null = null;
let lastResultsSignature = "";
let deployedVersion = "";
let lastPersonalChallengeKind = "";
let challengeArrivalTimer: number | null = null;
let submittedLootExpiresAt = 0;

function selectedSkin(): SurvivorSkin {
  return normalizeSurvivorSkin(localStorage.getItem("hotyb-skin"));
}
const skinGrid = document.getElementById("skin-grid");
if (skinGrid) {
  for (const button of skinGrid.querySelectorAll("button")) {
    button.classList.toggle("is-selected", button.getAttribute("data-skin") === selectedSkin());
    button.addEventListener("click", () => {
      localStorage.setItem("hotyb-skin", button.getAttribute("data-skin") ?? "jham");
      for (const other of skinGrid.querySelectorAll("button")) {
        other.classList.toggle("is-selected", other === button);
      }
    });
  }
}

document.getElementById("meta-xp")?.addEventListener("click", () => buyParkUpgrade("xp"));
document.getElementById("meta-regen")?.addEventListener("click", () => buyParkUpgrade("regen"));
renderParkUpgrades();

function storedStarterAbility(): AbilityId {
  const stored = localStorage.getItem("hotyb-ability");
  return ABILITIES.includes(stored as AbilityId) ? (stored as AbilityId) : "ember";
}

let selectedStarterAbility = storedStarterAbility();

function renderStarterSelection(): void {
  for (const starter of dom.starterGrid.querySelectorAll<HTMLButtonElement>(
    "button[data-ability]",
  )) {
    const selected = starter.dataset.ability === selectedStarterAbility;
    starter.classList.toggle("is-selected", selected);
    starter.setAttribute("aria-pressed", String(selected));
  }
  dom.selectedGearLabel.textContent =
    ABILITY_NAMES[selectedStarterAbility].toUpperCase() + " READY";
}

dom.starterGrid.addEventListener("click", (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
    "button[data-ability]",
  );
  if (!button) return;

  selectedStarterAbility = button.dataset.ability as AbilityId;
  localStorage.setItem("hotyb-ability", selectedStarterAbility);
  renderStarterSelection();
  sendTelemetry("gear_selected", {
    ability: selectedStarterAbility,
    msSinceLobby: Math.round(performance.now() - lobbyOpenedAt),
  });
});

dom.deployButton.addEventListener("click", () => {
  const ability = selectedStarterAbility;

  const name = dom.name.value.trim() || randomName();
  const session = localStorage.getItem("hotyb-session") ?? crypto.randomUUID();
  localStorage.setItem("hotyb-name", name);
  dom.name.value = name;
  dom.joinError.textContent = "";
  for (const starter of dom.starterGrid.querySelectorAll("button")) {
    starter.toggleAttribute("disabled", true);
  }
  dom.deployButton.disabled = true;

  trackRunStart(ability);
  connection.connect({ name, ability, session });
});

renderStarterSelection();

connection.addEventListener("server", (event) => {
  const message = (event as CustomEvent<ServerMessage>).detail;

  if (message.t === "welcome") {
    checkAppVersion(message.appVersion);
    dom.join.classList.add("is-hidden");
    dom.hud.classList.remove("is-hidden");
    dom.playerHud.classList.remove("is-hidden");
    dom.arenaMinimap.classList.remove("is-hidden");
    document.body.classList.add("game-active");
    dom.joinError.textContent = "";
    if (touchInput) {
      window.setTimeout(
        () =>
          showToast(
            "Use the stick or drag to move · ULT fires around you · DASH follows your direction",
          ),
        450,
      );
    }
    window.requestAnimationFrame(() => focusGameControls(true));
    return;
  }

  if (message.t === "snapshot") {
    checkAppVersion(message.appVersion);
    latestSnapshot = message;
    renderSnapshotHud(message);
    return;
  }

  if (message.t === "player") {
    if (playerState && message.hp < playerState.hp && !message.ghost) flashDamage();
    playerState = message;
    syncPendingLoot(message.pendingLoot);
    if (message.ghost) resetTouchMovement();
    renderPlayerHud(message);
    if (
      telemetryRunStartedAt &&
      !telemetryFirstLevelSent &&
      message.level > 1
    ) {
      telemetryFirstLevelSent = true;
      sendTelemetry("first_level", {
        ability: message.ability,
        level: message.level,
        runMs: Math.round(performance.now() - telemetryRunStartedAt),
      });
    }
    if (latestSnapshot?.roundResults) renderRoundResults(latestSnapshot);
    return;
  }

  if (message.t === "loot") {
    showLoot(message);
    return;
  }

  if (message.t === "raid") {
    showRaidWarning(message);
    return;
  }

  if (message.t === "pickup") return;

  if (message.t === "notice") {
    if (message.kind === "idle") {
      returnToMainScreen(message.text);
    } else if (message.kind === "victory" || message.kind === "defeat") {
      showResults(message.kind, message.text);
    } else {
      showToast(message.text);
    }
  }
});

function returnToMainScreen(message: string): void {
  connection.disconnect();
  hideLoot();
  classPanelOpen = false;
  playerState = null;
  latestSnapshot = null;
  lastStage = null;
  lastResultsSignature = "";
  submittedLootExpiresAt = 0;
  resetTouchMovement();

  if (raidWarningTimer) window.clearTimeout(raidWarningTimer);
  raidWarningTimer = null;
  raidWarningSequence += 1;
  activeRaidWarningKind = null;
  queuedRaidWarning = null;

  dom.hud.classList.add("is-hidden");
  dom.playerHud.classList.add("is-hidden");
  dom.lootPanel.classList.add("is-hidden");
  dom.classPanel.classList.add("is-hidden");
  dom.results.classList.add("is-hidden");
  dom.bossHud.classList.add("is-hidden");
  dom.personalChallenge.classList.add("is-hidden");
  dom.raidUltimate.classList.add("is-hidden");
  dom.teamBuffs.classList.add("is-hidden");
  dom.ghostPanel.classList.add("is-hidden");
  dom.arenaMinimap.classList.add("is-hidden");
  dom.arenaBoundary.classList.add("is-hidden");
  dom.raidWarning.classList.add("is-hidden");
  dom.raidWarning.classList.remove("show");
  dom.fogOverlay.classList.add("is-hidden");
  dom.join.classList.remove("is-hidden");
  dom.joinError.textContent = message;
  for (const starter of dom.starterGrid.querySelectorAll("button")) {
    starter.removeAttribute("disabled");
  }
  dom.deployButton.disabled = false;
  for (const toast of document.querySelectorAll(".toast")) toast.remove();
  document.body.classList.remove(
    "game-active",
    "loot-open",
    "class-open",
    "results-open",
    "overdrive-active",
    "team-surge-active",
  );
  window.requestAnimationFrame(() => dom.name.focus());
}

dom.versionRefresh.addEventListener("click", () => {
  if (deployedVersion) {
    localStorage.setItem("hotyb-dismissed-version", deployedVersion);
  }
  dom.versionBanner.classList.add("is-hidden");
  document.body.classList.remove("update-available");
  const url = new URL(window.location.href);
  url.searchParams.set(
    "build",
    (deployedVersion || "refresh") + "-" + Date.now(),
  );
  window.location.replace(url);
});

function checkAppVersion(serverVersion: string): void {
  if (!serverVersion || serverVersion <= __APP_VERSION__) return;
  const dismissedVersion = localStorage.getItem("hotyb-dismissed-version") ?? "";
  if (serverVersion <= dismissedVersion) return;
  deployedVersion = serverVersion;
  dom.versionBanner.classList.remove("is-hidden");
  document.body.classList.add("update-available");
}

async function pollForAppVersion(): Promise<void> {
  try {
    const response = await fetch(
      "/api/version?loaded=" + encodeURIComponent(__APP_VERSION__),
      { cache: "no-store" },
    );
    if (!response.ok) return;
    const payload = (await response.json()) as { appVersion?: string };
    if (payload.appVersion) checkAppVersion(payload.appVersion);
  } catch {
    // A version check should never interrupt the match or reconnect flow.
  }
}

void pollForAppVersion();
window.setInterval(() => void pollForAppVersion(), 10_000 + Math.random() * 2_000);
window.addEventListener("focus", () => void pollForAppVersion());
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") void pollForAppVersion();
});

window.addEventListener("keydown", (event) => {
  if (currentLoot) {
    const index = Number(event.key) - 1;
    if (index >= 0 && index < currentLoot.options.length) {
      chooseLoot(currentLoot.options[index]);
    }
    return;
  }

  if (latestSnapshot?.roundResults?.result === "victory") {
    const index = Number(event.key) - 1;
    const option = latestSnapshot.roundResults.artifactVotes[index]?.option;
    if (option) castVote(option.id);
  }

  if (event.key.toLowerCase() === "c" && playerState && !event.repeat) {
    toggleClassPanel();
  }
});

dom.abilityLabel.addEventListener("click", () => toggleClassPanel());

dom.readyButton.addEventListener("click", () => {
  if (!playerState || !latestSnapshot?.roundResults) return;
  const ready = !playerState.ready;
  connection.send({ t: "ready", ready });
  playerState.ready = ready;
  renderRoundResults(latestSnapshot, true);
});

dom.raidUltimateButton.addEventListener("click", () => {
  if (!playerState || !latestSnapshot?.raidUltimate[1]) return;
  const fire = !playerState.raidUltimateVoted;
  connection.send({ t: "raidUltimateVote", fire });
  playerState.raidUltimateVoted = fire;
  renderRaidUltimate(latestSnapshot);
});

dom.classCards.addEventListener("click", (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
    "button[data-switch-ability]",
  );
  if (!button || !playerState) return;
  switchAbility(button.dataset.switchAbility as AbilityId);
});

function renderSnapshotHud(snapshot: SnapshotMessage): void {
  if (lastStage && snapshot.stage !== lastStage) {
    const callout: Partial<Record<MatchStage, string>> = {
      escalation: "THE PARK STIRS — MORE DINOS INCOMING",
      chaos: "GREED UNBOUND — SURVIVE THE SWARM",
      descent: "A NEW TOUR BEGINS",
    };
    const text = callout[snapshot.stage];
    if (text) showToast(text);
    if (snapshot.stage === "boss") {
      game.scene.getScene("vault").cameras.main.shake(500, 0.01);
    }
  }
  lastStage = snapshot.stage;
  dom.round.textContent = String(snapshot.round);
  dom.playerCount.textContent = String(snapshot.players.length);
  dom.stage.textContent = stageName(snapshot.stage);
  dom.timer.textContent = formatTime(snapshot.remaining);
  dom.bossHud.classList.toggle("is-hidden", !snapshot.boss);
  if (snapshot.boss) {
    const [hp, maxHp, phase] = snapshot.boss;
    dom.bossMeter.style.width = percentage(hp / maxHp);
    dom.bossPhase.textContent = "PHASE " + phase;
  }
  renderRaidUltimate(snapshot);
  const [bankedMotes, requiredMotes] = snapshot.moteBank;
  dom.moteBank.textContent = bankedMotes + "/" + requiredMotes;
  const [bankedBait, requiredBait] = snapshot.baitBank;
  dom.baitBank.textContent = bankedBait + "/" + requiredBait;
  const overdriveRemaining = Math.max(
    0,
    snapshot.overdriveUntil - (Date.now() + connection.serverOffset),
  );
  dom.overdriveChip.classList.toggle("is-hidden", overdriveRemaining <= 0);
  dom.overdriveChip.textContent =
    "2× DAMAGE · 2× XP · MUST GO FASTER! " +
    (overdriveRemaining / 1_000).toFixed(1) +
    "s";
  document.body.classList.toggle("overdrive-active", overdriveRemaining > 0);
  const surgeRemaining = Math.max(
    0,
    snapshot.teamSurgeUntil - (Date.now() + connection.serverOffset),
  );
  dom.teamSurgeChip.classList.toggle("is-hidden", surgeRemaining <= 0);
  dom.teamSurgeChip.textContent =
    "⚡ GRID ONLINE · 2× DAMAGE · 2× XP · " +
    (surgeRemaining / 1_000).toFixed(1) +
    "s";
  document.body.classList.toggle("team-surge-active", surgeRemaining > 0);
  if (snapshot.teamCombo) {
    const [, , , progress, occupants, , captureMs] = snapshot.teamCombo;
    const required = snapshot.players.length === 1 ? 1 : 2;
    dom.teamComboChip.classList.remove("is-hidden");
    dom.teamComboChip.textContent =
      "▣ CREW COMBO " +
      occupants +
      "/" +
      required +
      " · " +
      Math.max(0, (captureMs / 1_000) * (1 - progress)).toFixed(1) +
      "s";
  } else {
    dom.teamComboChip.classList.add("is-hidden");
  }
  const [damageBuffs, hasteBuffs, vitalityBuffs, speedBuffs] = snapshot.teamBuffs;
  const truthActive =
    snapshot.teamTrueSightUntil > Date.now() + connection.serverOffset;
  const totalTeamBuffs =
    damageBuffs +
    hasteBuffs +
    vitalityBuffs +
    speedBuffs +
    snapshot.teamArtifacts.length +
    (truthActive ? 1 : 0);
  dom.teamBuffs.classList.toggle("is-hidden", totalTeamBuffs === 0);
  renderTeamBuff(dom.teamBuffDamage, damageBuffs);
  renderTeamBuff(dom.teamBuffHaste, hasteBuffs);
  renderTeamBuff(dom.teamBuffVitality, vitalityBuffs);
  renderTeamBuff(dom.teamBuffSpeed, speedBuffs);
  renderTeamBuff(dom.teamBuffArtifacts, snapshot.teamArtifacts.length);
  const truthCounter = dom.teamBuffTruesight.querySelector("i");
  if (truthCounter) truthCounter.textContent = truthActive ? "ON" : "OFF";
  dom.teamBuffTruesight.classList.toggle("inactive", !truthActive);
  dom.teamBuffArtifacts.title = snapshot.teamArtifactRounds
    .map(
      ([artifact, rounds]) =>
        ABILITY_NAMES[artifact] + " · " + rounds + " round" + (rounds === 1 ? "" : "s"),
    )
    .join("\n");

  const veilActive = snapshot.enemies.some((enemy) => enemy[1] === "veilkeeper");
  const localPlayer = snapshot.players.find((player) => player[0] === connection.localId);
  renderArenaNavigation(snapshot, localPlayer, veilActive);
  dom.fogOverlay.classList.toggle("is-hidden", !veilActive || !localPlayer);
  if (veilActive && localPlayer) {
    const camera = (game.scene.getScene("vault") as VaultScene).cameras.main;
    const canvasBounds = game.canvas.getBoundingClientRect();
    const screenX =
      canvasBounds.left +
      ((localPlayer[2] - camera.worldView.x) / camera.worldView.width) *
        canvasBounds.width;
    const screenY =
      canvasBounds.top +
      ((localPlayer[3] - camera.worldView.y) / camera.worldView.height) *
        canvasBounds.height;
    dom.fogOverlay.style.setProperty(
      "--fog-x",
      Phaser.Math.Clamp((screenX / window.innerWidth) * 100, 0, 100).toFixed(2) +
        "%",
    );
    dom.fogOverlay.style.setProperty(
      "--fog-y",
      Phaser.Math.Clamp((screenY / window.innerHeight) * 100, 0, 100).toFixed(2) +
        "%",
    );
  }
  dom.results.classList.toggle("is-hidden", snapshot.stage !== "results");
  document.body.classList.toggle("results-open", snapshot.stage === "results");
  if (snapshot.stage === "results") {
    dom.raidWarning.classList.add("is-hidden");
    dom.raidWarning.classList.remove("show");
    if (currentLoot) hideLoot();
    if (classPanelOpen) hideClassPanel();
    resetTouchMovement();
    renderRoundResults(snapshot);
  } else {
    lastResultsSignature = "";
  }
}

function renderArenaNavigation(
  snapshot: SnapshotMessage,
  localPlayer: PlayerTuple | undefined,
  veilActive: boolean,
): void {
  if (!localPlayer) {
    dom.arenaBoundary.classList.add("is-hidden");
    return;
  }

  const context = dom.arenaMinimap.getContext("2d");
  if (context) {
    const width = dom.arenaMinimap.width;
    const height = dom.arenaMinimap.height;
    const padding = 8;
    const mapWidth = width - padding * 2;
    const mapHeight = height - padding * 2;
    const [arenaX, arenaY, arenaWidth, arenaHeight] = snapshot.arena;
    const mapX = (worldX: number) =>
      padding + ((worldX - arenaX) / arenaWidth) * mapWidth;
    const mapY = (worldY: number) =>
      padding + ((worldY - arenaY) / arenaHeight) * mapHeight;
    const serverNow = Date.now() + connection.serverOffset;

    context.clearRect(0, 0, width, height);
    context.fillStyle = "rgba(8, 15, 10, 0.94)";
    context.fillRect(0, 0, width, height);
    context.fillStyle = "rgba(52, 88, 56, 0.25)";
    context.fillRect(padding, padding, mapWidth, mapHeight);
    context.strokeStyle = "rgba(249, 200, 14, 0.82)";
    context.lineWidth = 2;
    context.strokeRect(padding, padding, mapWidth, mapHeight);
    context.strokeStyle = "rgba(74, 161, 71, 0.4)";
    context.lineWidth = 1;
    context.beginPath();
    context.moveTo(mapX(arenaX + arenaWidth / 2), padding);
    context.lineTo(mapX(arenaX + arenaWidth / 2), height - padding);
    context.moveTo(padding, mapY(arenaY + arenaHeight / 2));
    context.lineTo(width - padding, mapY(arenaY + arenaHeight / 2));
    context.stroke();

    const camera = (game.scene.getScene("vault") as VaultScene).cameras.main;
    context.strokeStyle = "rgba(255, 255, 255, 0.72)";
    context.lineWidth = 1;
    context.strokeRect(
      mapX(camera.worldView.x),
      mapY(camera.worldView.y),
      (camera.worldView.width / arenaWidth) * mapWidth,
      (camera.worldView.height / arenaHeight) * mapHeight,
    );

    context.fillStyle = "#ffd66b";
    context.beginPath();
    context.arc(mapX(AMBER_CACHE_X), mapY(AMBER_CACHE_Y), 3, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = "#ff765c";
    context.beginPath();
    context.arc(mapX(BAIT_PEN_X), mapY(BAIT_PEN_Y), 3, 0, Math.PI * 2);
    context.fill();

    if (snapshot.teamCombo) {
      const [x, y, size] = snapshot.teamCombo;
      context.strokeStyle = "#f9c80e";
      context.lineWidth = 1.5;
      context.strokeRect(
        mapX(x - size / 2),
        mapY(y - size / 2),
        (size / arenaWidth) * mapWidth,
        (size / arenaHeight) * mapHeight,
      );
    }

    for (const [x, y] of snapshot.triangleRally?.[0] ?? []) {
      context.fillStyle = "#ff8fd1";
      context.fillRect(mapX(x) - 2, mapY(y) - 2, 4, 4);
    }

    for (const [
      ,
      kind,
      x,
      y,
      radius,
      triggerAt,
      ,
      startAngle,
      angularSpeed,
      ,
      directionX,
      directionY,
      gapCenter,
      gapSize,
    ] of snapshot.hazards) {
      if (kind === "army") {
        const vertical = directionX !== 0;
        const direction = vertical ? directionX : directionY;
        const travelSeconds = Math.max(0, serverNow - triggerAt) / 1_000;
        const rawPosition = vertical
          ? x + directionX * angularSpeed * travelSeconds
          : y + directionY * angularSpeed * travelSeconds;
        const minimum = vertical ? arenaX : arenaY;
        const maximum = vertical ? arenaX + arenaWidth : arenaY + arenaHeight;
        const afterExit = direction > 0
          ? rawPosition > maximum
          : rawPosition < minimum;
        if (afterExit) continue;
        const beforeEntry = direction > 0
          ? rawPosition < minimum
          : rawPosition > maximum;
        const position = beforeEntry
          ? direction > 0
            ? minimum
            : maximum
          : rawPosition;
        const gapStart = gapCenter - gapSize / 2;
        const gapEnd = gapCenter + gapSize / 2;
        context.strokeStyle = serverNow >= triggerAt ? "#ff385e" : "#ffa052";
        context.lineWidth = serverNow >= triggerAt ? 3 : 2;
        context.beginPath();
        if (vertical) {
          context.moveTo(mapX(position), mapY(arenaY));
          context.lineTo(mapX(position), mapY(gapStart));
          context.moveTo(mapX(position), mapY(gapEnd));
          context.lineTo(mapX(position), mapY(arenaY + arenaHeight));
        } else {
          context.moveTo(mapX(arenaX), mapY(position));
          context.lineTo(mapX(gapStart), mapY(position));
          context.moveTo(mapX(gapEnd), mapY(position));
          context.lineTo(mapX(arenaX + arenaWidth), mapY(position));
        }
        context.stroke();
        context.strokeStyle = "#8dffcf";
        context.lineWidth = 3;
        context.beginPath();
        if (vertical) {
          context.moveTo(mapX(position), mapY(gapStart));
          context.lineTo(mapX(position), mapY(gapEnd));
        } else {
          context.moveTo(mapX(gapStart), mapY(position));
          context.lineTo(mapX(gapEnd), mapY(position));
        }
        context.stroke();
      } else if (kind === "collapse") {
        context.fillStyle = "rgba(100, 245, 186, 0.16)";
        context.strokeStyle = serverNow >= triggerAt ? "#ffffff" : "#ffd66b";
        context.lineWidth = 2;
        context.beginPath();
        context.ellipse(
          mapX(x),
          mapY(y),
          (radius / arenaWidth) * mapWidth,
          (radius / arenaHeight) * mapHeight,
          0,
          0,
          Math.PI * 2,
        );
        context.fill();
        context.stroke();
      } else if (kind === "laser") {
        const angle =
          startAngle +
          angularSpeed * (Math.max(0, serverNow - triggerAt) / 1_000);
        context.strokeStyle = serverNow >= triggerAt ? "#ffb347" : "#ff6b52";
        context.lineWidth = serverNow >= triggerAt ? 2.5 : 1.5;
        context.beginPath();
        context.moveTo(mapX(x), mapY(y));
        context.lineTo(
          mapX(x + Math.cos(angle) * radius),
          mapY(y + Math.sin(angle) * radius),
        );
        context.stroke();
      }
    }

    for (const enemy of snapshot.enemies) {
      const kind = enemy[1];
      if (
        kind !== "boss" &&
        kind !== "guardian" &&
        kind !== "gelatinous" &&
        kind !== "miniboss" &&
        !isNamedEnemy(kind)
      ) {
        continue;
      }
      context.fillStyle =
        kind === "gelatinous"
          ? "#69f28b"
          : kind === "boss"
            ? "#ff4e69"
            : kind === "guardian"
              ? "#ffd66b"
              : "#d58cff";
      context.beginPath();
      context.arc(
        mapX(enemy[2]),
        mapY(enemy[3]),
        kind === "boss" || kind === "gelatinous" ? 4 : 2.5,
        0,
        Math.PI * 2,
      );
      context.fill();
    }

    for (const player of snapshot.players) {
      context.fillStyle =
        player[0] === connection.localId
          ? "#fff1a6"
          : player[8]
            ? "rgba(190, 180, 210, 0.45)"
            : "rgba(105, 231, 255, 0.78)";
      context.beginPath();
      context.arc(
        mapX(player[2]),
        mapY(player[3]),
        player[0] === connection.localId ? 3.6 : 1.5,
        0,
        Math.PI * 2,
      );
      context.fill();
    }

    if (veilActive) {
      context.fillStyle = "rgba(0, 0, 0, 0.985)";
      context.fillRect(padding, padding, mapWidth, mapHeight);
      context.strokeStyle = "rgba(178, 200, 158, 0.9)";
      context.lineWidth = 2;
      context.strokeRect(padding, padding, mapWidth, mapHeight);
      context.fillStyle = "#d8d2f0";
      context.font = "700 15px 'DM Mono', monospace";
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillText("SIGNAL LOST", width / 2, height / 2 - 8);
      context.fillStyle = "rgba(216, 210, 240, 0.7)";
      context.font = "10px 'DM Mono', monospace";
      context.fillText("KILL VEILKEEPER", width / 2, height / 2 + 13);
    }
  }

  const [arenaX, arenaY, arenaWidth, arenaHeight] = snapshot.arena;
  const edgeDistances = [
    ["WEST", localPlayer[2] - (arenaX + 24)],
    ["EAST", arenaX + arenaWidth - 24 - localPlayer[2]],
    ["NORTH", localPlayer[3] - (arenaY + 24)],
    ["SOUTH", arenaY + arenaHeight - 24 - localPlayer[3]],
  ] as const;
  const threshold = 420;
  const sortedEdges = [...edgeDistances].sort((a, b) => a[1] - b[1]);
  const nearestDistance = Math.max(0, sortedEdges[0][1]);
  const nearbyEdges = sortedEdges
    .filter(([, distance]) => distance <= threshold && distance <= nearestDistance + 90)
    .map(([direction]) => direction);
  const edgeColor = (distance: number) => {
    const strength = Phaser.Math.Clamp(1 - Math.max(0, distance) / threshold, 0, 1);
    return `rgba(255, 84, 111, ${(strength * 0.34).toFixed(3)})`;
  };
  for (const [direction, distance] of edgeDistances) {
    dom.arenaBoundary.style.setProperty(
      "--edge-" + direction.toLowerCase(),
      edgeColor(distance),
    );
  }
  const nearBoundary = nearbyEdges.length > 0;
  dom.arenaBoundary.classList.toggle("is-hidden", !nearBoundary);
  if (nearBoundary) {
    dom.arenaBoundaryLabel.textContent =
      nearbyEdges.join(" + ") + " PERIMETER FENCE" + (nearbyEdges.length > 1 ? "S" : "");
    dom.arenaBoundaryDistance.textContent =
      Math.ceil(nearestDistance / 10) * 10 + " units away";
  }
}

function renderPlayerHud(state: PlayerStateMessage): void {
  document.body.classList.toggle("player-down", state.ghost);
  dom.healthMeter.style.width = percentage(state.hp / state.maxHp);
  dom.healthLabel.textContent = Math.ceil(state.hp) + "/" + state.maxHp;
  dom.xpMeter.style.width = percentage(state.xp / state.nextXp);
  dom.xpLabel.textContent = state.xp + "/" + state.nextXp;
  dom.levelLabel.textContent = String(state.level);
  dom.abilityLabel.textContent = ABILITY_NAMES[state.ability].toUpperCase() + " · SWITCH";
  dom.specialName.textContent = ULTIMATE_NAMES[state.ability].toUpperCase();
  dom.touchSpecialName.textContent = ULTIMATE_NAMES[state.ability].toUpperCase();
  dom.touchBomb.setAttribute(
    "aria-label",
    "Use " + ULTIMATE_NAMES[state.ability] + " centered on your survivor",
  );
  dom.abilityLabel.style.setProperty(
    "--ability-color",
    "#" + ABILITY_COLORS[state.ability].toString(16).padStart(6, "0"),
  );
  dom.ghostPanel.classList.toggle("is-hidden", !state.ghost);
  dom.ghostCause.textContent =
    "LAST HIT: " + (state.downedBy || "UNKNOWN PARK HAZARD").toUpperCase();
  dom.relicCount.textContent =
    "◇ " +
    state.artifacts.length +
    (state.artifacts.length === 1 ? " PROTOTYPE" : " PROTOTYPES") +
    (latestSnapshot?.teamArtifactRounds.length
      ? " · " +
        Math.min(...latestSnapshot.teamArtifactRounds.map(([, rounds]) => rounds)) +
        "R"
      : "");
  dom.relicCount.title = (latestSnapshot?.teamArtifactRounds ?? [])
    .map(([artifact, rounds]) => ABILITY_NAMES[artifact] + " · " + rounds + " rounds left")
    .join(" + ");
  dom.relicCount.classList.toggle("is-hidden", state.artifacts.length === 0);
  dom.moteCount.textContent = state.motes + "/" + state.moteCapacity;
  dom.baitCount.textContent = state.bait + "/" + state.baitCapacity;
  const serverNow = Date.now() + connection.serverOffset;
  const challenge = state.personalChallenge;
  const challengeCompleteAge = challenge.completedAt
    ? serverNow - challenge.completedAt
    : 0;
  dom.personalChallenge.classList.remove("is-hidden");
  dom.personalChallenge.classList.toggle("complete", challenge.completed);
  dom.personalChallenge.classList.toggle(
    "departing",
    challenge.completed &&
      challenge.completedAt > 0 &&
      challengeCompleteAge >= PERSONAL_CHALLENGE_COMPLETE_DISPLAY_MS,
  );
  if (challenge.kind !== lastPersonalChallengeKind) {
    lastPersonalChallengeKind = challenge.kind;
    dom.personalChallenge.classList.remove("arriving");
    void dom.personalChallenge.offsetWidth;
    dom.personalChallenge.classList.add("arriving");
    if (challengeArrivalTimer) window.clearTimeout(challengeArrivalTimer);
    challengeArrivalTimer = window.setTimeout(() => {
      dom.personalChallenge.classList.remove("arriving");
      challengeArrivalTimer = null;
    }, 650);
  }
  dom.challengeName.textContent = challenge.name.toUpperCase();
  dom.challengeCopy.textContent = challenge.completed
    ? "Complete · rare field gear awarded · new challenge incoming"
    : challenge.description;
  dom.challengeStatus.textContent =
    Math.floor(challenge.progress) + "/" + challenge.goal;
  dom.challengeMeter.style.width = percentage(
    challenge.progress / Math.max(1, challenge.goal),
  );
  dom.evolutionChip.classList.toggle("is-hidden", !state.evolution);
  dom.evolutionChip.textContent = state.evolution
    ? "EVOLVED · " + state.evolution.name.toUpperCase()
    : "";
  dom.evolutionChip.title = state.evolution?.description ?? "";
  const statuses: string[] = [];
  if (state.ultimateCharges > 0) {
    statuses.push("ULTIMATE BARRAGE ×" + state.ultimateCharges);
  }
  if (state.rodUntil > serverNow) {
    statuses.push(
      "BINGO! DINO DNA! · " +
        ((state.rodUntil - serverNow) / 1_000).toFixed(1) +
        "s",
    );
  } else {
    if (state.furyUntil > serverNow) statuses.push("FURY");
    if (state.hasteUntil > serverNow) statuses.push("HASTE");
    if (state.shieldUntil > serverNow) statuses.push("BODY ARMOR");
  }
  if (state.snackRegenUntil > serverNow) {
    statuses.push(
      "JELL-O REGEN · " +
        ((state.snackRegenUntil - serverNow) / 1_000).toFixed(1) +
        "s",
    );
  }
  if (state.seaBassUntil > serverNow) {
    statuses.push(
      "CHILEAN SEA BASS · " +
        ((state.seaBassUntil - serverNow) / 1_000).toFixed(1) +
        "s",
    );
  }
  if (state.energyBarUntil > serverNow) {
    statuses.push(
      "ENERGY BAR · " +
        ((state.energyBarUntil - serverNow) / 1_000).toFixed(1) +
        "s",
    );
  }
  if (state.icePopUntil > serverNow) {
    statuses.push(
      "ICE POP AURA · " +
        ((state.icePopUntil - serverNow) / 1_000).toFixed(1) +
        "s",
    );
  }
  if (state.canteenUntil > serverNow) {
    statuses.push(
      "FIELD CANTEEN · " +
        ((state.canteenUntil - serverNow) / 1_000).toFixed(1) +
        "s",
    );
  }
  dom.powerupChip.classList.toggle("is-hidden", statuses.length === 0);
  dom.powerupChip.textContent = statuses.join(" · ");
  dom.powerupChip.classList.toggle("rod", state.rodUntil > serverNow);
  dom.powerupChip.classList.toggle(
    "barrage",
    state.ultimateCharges > 0,
  );
  if (latestSnapshot) renderRaidUltimate(latestSnapshot);
  updateSpecialHud();
  if (state.ghost) updateRespawnTimer();
}

function renderRaidUltimate(snapshot: SnapshotMessage): void {
  const [charge, ready, votes, requiredVotes] = snapshot.raidUltimate;
  dom.raidUltimate.classList.toggle("is-hidden", snapshot.stage === "results");
  dom.raidUltimate.classList.toggle("ready", ready);
  dom.raidUltimateMeter.style.width = percentage(charge / 100);
  dom.raidUltimateLabel.textContent = ready
    ? votes + "/" + requiredVotes + " VOTES"
    : Math.floor(charge) + "%";
  dom.raidUltimateButton.disabled = !ready;
  dom.raidUltimateButton.classList.toggle(
    "voted",
    ready && playerState?.raidUltimateVoted === true,
  );
  dom.raidUltimateButton.textContent = !ready
    ? "TAKE THE SHOT"
    : playerState?.raidUltimateVoted
      ? "SHOT CALLED ✓"
      : "VOTE: SHOOT";
}

function updateSpecialHud(): void {
  if (!playerState) return;
  const remaining = Math.max(
    0,
    playerState.specialReadyAt - (Date.now() + connection.serverOffset),
  );
  const ready = remaining === 0 && !playerState.ghost;
  dom.specialChip.classList.toggle("ready", ready);
  const cooldownText = ready
    ? playerState.ultimateCharges > 0
      ? "×" + playerState.ultimateCharges + " READY"
      : "READY"
    : playerState.ghost
      ? "DOWN"
      : (remaining / 1_000).toFixed(1) + "s";
  dom.specialCooldown.textContent = cooldownText;
  dom.touchSpecialCooldown.textContent = cooldownText;
  dom.touchBomb.disabled = !ready;
  dom.touchBomb.classList.toggle("ready", ready);
}

function renderTeamBuff(element: HTMLElement, stacks: number): void {
  const counter = element.querySelector("i");
  if (counter) counter.textContent = "×" + stacks;
  element.classList.toggle("inactive", stacks === 0);
}

function castVote(id: LootId): void {
  if (!playerState || latestSnapshot?.roundResults?.result !== "victory") return;
  connection.send({ t: "vote", id });
  playerState.resultVote = id;
  playerState.ready = false;
  if (latestSnapshot) renderRoundResults(latestSnapshot, true);
}


// ---- Anonymous meta progression (M4): amber wallet + park upgrades ----
function readMetaNumber(key: string, max: number): number {
  const raw = Number(localStorage.getItem(key) ?? "0");
  return Number.isFinite(raw) ? Math.min(max, Math.max(0, Math.floor(raw))) : 0;
}
function metaAmber(): number {
  return readMetaNumber("hotyb-amber", Number.MAX_SAFE_INTEGER);
}
function metaXpLevel(): number {
  return readMetaNumber("hotyb-meta-xp", 1);
}
function metaRegenLevel(): number {
  return readMetaNumber("hotyb-meta-regen", 3);
}
function renderParkUpgrades(): void {
  const balance = document.getElementById("amber-balance");
  const xpState = document.getElementById("meta-xp-state");
  const regenState = document.getElementById("meta-regen-state");
  const xpButton = document.getElementById("meta-xp") as HTMLButtonElement | null;
  const regenButton = document.getElementById("meta-regen") as HTMLButtonElement | null;
  if (!balance || !xpState || !regenState || !xpButton || !regenButton) return;
  const amber = metaAmber();
  const xp = metaXpLevel();
  const regen = metaRegenLevel();
  balance.textContent = String(amber);
  xpState.textContent = xp >= 1 ? "OWNED" : "50 AMBER";
  const regenCost = 40 * (regen + 1);
  regenState.textContent = regen >= 3 ? "MAX (3/3)" : regen + "/3 · " + regenCost + " AMBER";
  xpButton.disabled = xp >= 1 || amber < 50;
  regenButton.disabled = regen >= 3 || amber < regenCost;
}
function buyParkUpgrade(kind: "xp" | "regen"): void {
  const amber = metaAmber();
  if (kind === "xp" && metaXpLevel() < 1 && amber >= 50) {
    localStorage.setItem("hotyb-amber", String(amber - 50));
    localStorage.setItem("hotyb-meta-xp", "1");
  }
  if (kind === "regen" && metaRegenLevel() < 3) {
    const cost = 40 * (metaRegenLevel() + 1);
    if (amber >= cost) {
      localStorage.setItem("hotyb-amber", String(amber - cost));
      localStorage.setItem("hotyb-meta-regen", String(metaRegenLevel() + 1));
    }
  }
  renderParkUpgrades();
}
let lastAmberAwardKey = localStorage.getItem("hotyb-amber-award-key") ?? "";
function awardRunAmber(round: number, endsAt: number, kills: number, banked: number): void {
  const key = round + ":" + endsAt;
  if (key === lastAmberAwardKey) return;
  lastAmberAwardKey = key;
  localStorage.setItem("hotyb-amber-award-key", key);
  const earned = Math.max(1, banked * 3 + Math.floor(kills / 3));
  localStorage.setItem("hotyb-amber", String(metaAmber() + earned));
  renderParkUpgrades();
}

function renderRoundResults(snapshot: SnapshotMessage, force = false): void {
  const results = snapshot.roundResults;
  if (!results) return;
  const remaining = Math.max(
    0,
    results.endsAt - (Date.now() + connection.serverOffset),
  );
  dom.resultsCountdown.textContent = String(Math.ceil(remaining / 1_000));
  dom.readyCount.textContent = results.readyCount + "/" + results.playerCount;
  dom.resultRound.textContent = String(results.round);
  dom.resultTitle.textContent =
    results.result === "victory" ? "THE PARK IS YOURS" : "GAME OVER";
  dom.resultCopy.textContent =
    results.result === "victory"
      ? "Your levels carry forward. The winning prototype equips to the Park Crew for the next 3 rounds."
      : "The campaign ends here. The next tour restarts the expedition at level 1.";
  dom.artifactVotePanel.classList.toggle("is-hidden", results.result !== "victory");
  dom.readyButton.disabled =
    results.result === "victory" && !playerState?.resultVote;
  dom.readyButton.classList.toggle("ready", playerState?.ready === true);
  dom.readyButton.textContent = playerState?.ready ? "READY ✓" : "READY UP";

  const localRow = results.leaderboard.find((row) => row[0] === connection.localId);
  if (localRow) {
    awardRunAmber(results.round, results.endsAt, localRow[4], localRow[6]);
    const telemetryKey = results.round + ":" + results.endsAt;
    if (telemetryKey !== telemetryRunEndKey) {
      telemetryRunEndKey = telemetryKey;
      sendTelemetry("run_end", {
        result: results.result,
        round: results.round,
        ability: localRow[2],
        level: localRow[7],
        kills: localRow[4],
        deaths:
          snapshot.players.find((player) => player[0] === connection.localId)?.[9] ??
          0,
        runMs: telemetryRunStartedAt
          ? Math.round(performance.now() - telemetryRunStartedAt)
          : snapshot.elapsed,
        matchMs: snapshot.elapsed,
      });
    }
  }
  const signature = JSON.stringify([
    results.result,
    results.leaderboard,
    results.medals,
    results.artifactVotes.map(({ option, votes, voters }) => [
      option.id,
      votes,
      voters.map(([id]) => id),
    ]),
    playerState?.resultVote,
    playerState?.ready,
  ]);
  if (!force && signature === lastResultsSignature) return;
  lastResultsSignature = signature;

  dom.leaderboardRows.replaceChildren(
    ...results.leaderboard.map((entry, index) => {
      const [id, name, ability, damage, kills, revives, motes, level] = entry;
      const row = document.createElement("div");
      row.className = "leaderboard-row" + (id === connection.localId ? " self" : "");
      row.innerHTML =
        "<span class=\"looter-name\">" +
        (index + 1) +
        " · " +
        escapeHtml(abilityGlyph(ability) + " " + name) +
        " <small>LV" +
        level +
        "</small></span><span>" +
        compactNumber(damage) +
        "</span><span>" +
        kills +
        "</span><span>" +
        revives +
        "</span><span>" +
        motes +
        "</span>";
      return row;
    }),
  );

  dom.roundMedals.replaceChildren(
    ...results.medals.map(([id, icon, title, winnerId, winnerName, value]) => {
      const medal = document.createElement("div");
      medal.className =
        "round-medal medal-" +
        id +
        (winnerId === connection.localId ? " self" : "");
      medal.innerHTML =
        "<span>" +
        escapeHtml(icon) +
        "</span><div><b>" +
        escapeHtml(title) +
        "</b><strong>" +
        escapeHtml(winnerName) +
        "</strong><small>" +
        escapeHtml(value) +
        "</small></div>";
      return medal;
    }),
  );

  dom.artifactVoteCards.replaceChildren(
    ...results.artifactVotes.map(({ option, votes, voters }, index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className =
        "artifact-vote-card" +
        (playerState?.resultVote === option.id ? " selected" : "");
      const voterNames = voters
        .map(([, name, ability]) => abilityGlyph(ability) + " " + name)
        .join(", ");
      button.title = voterNames || "No votes yet";
      button.innerHTML =
        "<span class=\"vote-icon\">" +
        escapeHtml(option.icon) +
        "</span><span><b>" +
        (index + 1) +
        " · " +
        escapeHtml(option.name) +
        "</b><small>" +
        escapeHtml(option.description) +
        "</small><span class=\"vote-voters\">" +
        escapeHtml(voterNames || "Waiting for votes…") +
        "</span></span><span class=\"vote-count\">" +
        votes +
        "</span>";
      button.addEventListener("click", () => castVote(option.id));
      return button;
    }),
  );
}

function compactNumber(value: number): string {
  if (value >= 1_000_000) return (value / 1_000_000).toFixed(1) + "m";
  if (value >= 10_000) return Math.round(value / 1_000) + "k";
  if (value >= 1_000) return (value / 1_000).toFixed(1) + "k";
  return String(value);
}

function showLoot(message: LootMessage): void {
  if (document.body.classList.contains("results-open")) return;
  if (
    currentLoot?.expiresAt === message.expiresAt &&
    currentLoot.source === message.source
  ) {
    return;
  }
  hideClassPanel();
  currentLoot = message;
  dom.lootSource.textContent =
    message.source === "boss"
      ? "YOUR PERSONAL PROTOTYPE · ONLY YOU RECEIVE IT"
      : message.source === "cube"
        ? "ANKYLO DROP · BONUS PICK"
      : message.source === "challenge"
        ? "CHALLENGE COMPLETE · GUARANTEED RARE"
      : message.source === "grave"
      ? "RANGER CACHE"
      : message.source === "elite"
        ? "ALPHA SUPPLY CACHE"
        : "LEVEL UP";
  dom.lootCards.replaceChildren(
    ...message.options.map((option, index) => {
      const button = document.createElement("button");
      button.className = "loot-card " + option.rarity;
      button.title = option.name + " — " + option.description;
      button.innerHTML =
        "<span class=\"loot-key\">" +
        (index + 1) +
        "</span><div><b>" +
        escapeHtml(option.name) +
        "</b><span>" +
        escapeHtml(option.description) +
        "</span></div>" +
        (message.personal
          ? "<span class=\"personal-badge\">PERSONAL</span>"
          : "");
      button.addEventListener("click", () => chooseLoot(option));
      return button;
    }),
  );
  dom.lootPanel.classList.remove("is-hidden");
  dom.lootProtection.classList.toggle("is-hidden", message.source === "grave");
  setLootLayout(true);

  if (lootCountdown) window.clearInterval(lootCountdown);
  const update = () => {
    const remainingMs = message.expiresAt - (Date.now() + connection.serverOffset);
    const remaining = Math.max(0, Math.ceil(remainingMs / 1000));
    dom.lootTimer.textContent = String(remaining);
    if (remainingMs <= 0 && currentLoot === message) {
      chooseLoot(message.options[0], true);
    }
  };
  update();
  lootCountdown = window.setInterval(update, 250);
}

function chooseLoot(option: LootOption, automatic = false): void {
  const personalArtifact = currentLoot?.source === "boss";
  submittedLootExpiresAt = currentLoot?.expiresAt ?? 0;
  connection.send({ t: "choose", id: option.id });
  showToast(
    (automatic ? "Time's up — " : "") +
      option.name +
      (personalArtifact ? " equipped to YOU" : " acquired"),
  );
  if (personalArtifact) {
    dom.resultCopy.textContent =
      option.name + " is yours alone. Other survivors are choosing their own prototypes.";
  }
  hideLoot();
  window.requestAnimationFrame(() => focusGameControls());
}

function hideLoot(): void {
  currentLoot = null;
  dom.lootPanel.classList.add("is-hidden");
  dom.lootProtection.classList.add("is-hidden");
  setLootLayout(false);
  if (lootCountdown) window.clearInterval(lootCountdown);
  lootCountdown = null;
}

function syncPendingLoot(pending: LootMessage | null | undefined): void {
  if (pending === undefined) return;
  if (pending) {
    if (pending.expiresAt !== submittedLootExpiresAt) showLoot(pending);
    return;
  }
  if (currentLoot) hideLoot();
  submittedLootExpiresAt = 0;
}

function setLootLayout(open: boolean): void {
  document.body.classList.toggle("loot-open", open);
  if (open) {
    resetTouchMovement();
  }
}

function toggleClassPanel(): void {
  if (!playerState || currentLoot) return;
  if (classPanelOpen) {
    hideClassPanel();
    return;
  }

  classPanelOpen = true;
  document.body.classList.add("class-open");
  dom.classSummary.textContent =
    "LEVEL " + playerState.level + " KEPT · XP AND ALL UPGRADES KEPT";
  for (const button of dom.classCards.querySelectorAll<HTMLButtonElement>(
    "button[data-switch-ability]",
  )) {
    const active = button.dataset.switchAbility === playerState.ability;
    button.setAttribute("aria-pressed", String(active));
    button.toggleAttribute("disabled", active);
  }
  dom.classPanel.classList.remove("is-hidden");
  setLootLayout(true);
  window.requestAnimationFrame(() => focusGameControls());
}

function hideClassPanel(): void {
  classPanelOpen = false;
  document.body.classList.remove("class-open");
  dom.classPanel.classList.add("is-hidden");
  setLootLayout(Boolean(currentLoot));
  window.requestAnimationFrame(() => focusGameControls());
}

function switchAbility(ability: AbilityId): void {
  if (!playerState || ability === playerState.ability) return;
  connection.rememberAbility(ability);
  connection.send({ t: "switch", ability });
  playerState.ability = ability;
  renderPlayerHud(playerState);
  showToast("Switched to " + ABILITY_NAMES[ability] + " — level preserved");
  hideClassPanel();
  window.requestAnimationFrame(() => focusGameControls());
}

function focusGameControls(resetKeys = false): void {
  if (!dom.join.classList.contains("is-hidden")) return;
  game.canvas.focus({ preventScroll: true });
  if (resetKeys) {
    game.scene.getScene("vault").input.keyboard?.resetKeys();
  }
}

function setupTouchControls(): void {
  let stickPointerId: number | null = null;
  const updateStick = (event: PointerEvent) => {
    const bounds = dom.touchStick.getBoundingClientRect();
    const dx = event.clientX - (bounds.left + bounds.width / 2);
    const dy = event.clientY - (bounds.top + bounds.height / 2);
    const maxDistance = bounds.width * 0.34;
    const distance = Math.hypot(dx, dy);
    const scale = distance > maxDistance ? maxDistance / distance : 1;
    const knobX = dx * scale;
    const knobY = dy * scale;
    const direction = touchDragDirection(
      dx,
      dy,
      maxDistance * 0.14,
      maxDistance,
    );
    arenaTouch.x = 0;
    arenaTouch.y = 0;
    arenaTouch.targetX = null;
    arenaTouch.targetY = null;
    mobileMovement.x = direction?.x ?? 0;
    mobileMovement.y = direction?.y ?? 0;
    dom.touchKnob.style.transform =
      "translate(" + knobX.toFixed(1) + "px, " + knobY.toFixed(1) + "px)";
  };
  const releaseStick = (event?: PointerEvent) => {
    if (event && stickPointerId !== event.pointerId) return;
    stickPointerId = null;
    mobileMovement.x = 0;
    mobileMovement.y = 0;
    dom.touchKnob.style.transform = "translate(0px, 0px)";
  };

  dom.touchStick.addEventListener("pointerdown", (event) => {
    event.preventDefault();
    stickPointerId = event.pointerId;
    dom.touchStick.setPointerCapture(event.pointerId);
    updateStick(event);
  });
  dom.touchStick.addEventListener("pointermove", (event) => {
    if (stickPointerId === event.pointerId) updateStick(event);
  });
  dom.touchStick.addEventListener("pointerup", releaseStick);
  dom.touchStick.addEventListener("pointercancel", releaseStick);
  window.addEventListener("blur", resetTouchMovement);

  const canvas = game.canvas;
  const arenaPoint = (event: PointerEvent) => {
    const bounds = canvas.getBoundingClientRect();
    const camera = (game.scene.getScene("vault") as VaultScene).cameras.main;
    const viewportPoint = canvasPointToArena(
      event.clientX,
      event.clientY,
      bounds,
      camera.worldView.width,
      camera.worldView.height,
    );
    return viewportPointToWorld(
      viewportPoint,
      camera.worldView.x,
      camera.worldView.y,
      ARENA_WIDTH,
      ARENA_HEIGHT,
    );
  };
  const acceptsArenaTouch = (event: PointerEvent) =>
    (touchPreview || event.pointerType === "touch" || event.pointerType === "pen") &&
    stickPointerId === null &&
    document.body.classList.contains("game-active") &&
    !document.body.classList.contains("loot-open") &&
    !document.body.classList.contains("class-open") &&
    !document.body.classList.contains("results-open") &&
    !playerState?.ghost;

  canvas.addEventListener("pointerdown", (event) => {
    if (!acceptsArenaTouch(event) || arenaTouch.pointerId !== null) return;
    event.preventDefault();
    const point = arenaPoint(event);
    arenaTouch.pointerId = event.pointerId;
    arenaTouch.startClientX = event.clientX;
    arenaTouch.startClientY = event.clientY;
    arenaTouch.x = 0;
    arenaTouch.y = 0;
    arenaTouch.targetX = point.x;
    arenaTouch.targetY = point.y;
    mobileMovement.x = 0;
    mobileMovement.y = 0;
    dom.touchKnob.style.transform = "translate(0px, 0px)";
    try {
      canvas.setPointerCapture(event.pointerId);
    } catch {
      // Some embedded mobile browsers do not expose pointer capture.
    }
  });
  canvas.addEventListener("pointermove", (event) => {
    if (arenaTouch.pointerId !== event.pointerId) return;
    event.preventDefault();
    const dx = event.clientX - arenaTouch.startClientX;
    const dy = event.clientY - arenaTouch.startClientY;
    const direction = touchDragDirection(dx, dy);
    if (!direction) return;
    arenaTouch.targetX = null;
    arenaTouch.targetY = null;
    arenaTouch.x = direction.x;
    arenaTouch.y = direction.y;
  });
  const releaseArenaTouch = (event: PointerEvent, cancelled = false) => {
    if (arenaTouch.pointerId !== event.pointerId) return;
    const wasDragging = arenaTouch.x !== 0 || arenaTouch.y !== 0;
    arenaTouch.pointerId = null;
    arenaTouch.x = 0;
    arenaTouch.y = 0;
    if (cancelled || wasDragging) {
      arenaTouch.targetX = null;
      arenaTouch.targetY = null;
    }
  };
  canvas.addEventListener("pointerup", (event) => releaseArenaTouch(event));
  canvas.addEventListener("pointercancel", (event) => releaseArenaTouch(event, true));

  dom.touchBomb.addEventListener("pointerdown", (event) => {
    event.preventDefault();
    (game.scene.getScene("vault") as VaultScene).fireLootBomb(true);
  });
  dom.touchDash.addEventListener("pointerdown", (event) => {
    event.preventDefault();
    (game.scene.getScene("vault") as VaultScene).startLocalDash();
  });
}

function resetTouchMovement(): void {
  mobileMovement.x = 0;
  mobileMovement.y = 0;
  arenaTouch.pointerId = null;
  arenaTouch.x = 0;
  arenaTouch.y = 0;
  arenaTouch.targetX = null;
  arenaTouch.targetY = null;
  dom.touchKnob.style.transform = "translate(0px, 0px)";
}

function updateRespawnTimer(): void {
  if (!playerState?.ghost) return;
  const remaining = Math.max(
    0,
    Math.ceil((playerState.respawnAt - (Date.now() + connection.serverOffset)) / 1000),
  );
  dom.respawnTimer.textContent = String(remaining);
  if (remaining > 0) window.setTimeout(updateRespawnTimer, 250);
}

function showResults(result: "victory" | "defeat", text: string): void {
  hideLoot();
  if (classPanelOpen) hideClassPanel();
  resetTouchMovement();
  for (const toast of document.querySelectorAll(".toast")) toast.remove();
  dom.results.classList.remove("is-hidden");
  dom.resultTitle.textContent =
    result === "victory" ? "THE PARK IS YOURS" : "GAME OVER";
  dom.resultCopy.textContent =
    text +
    (result === "victory"
      ? " Vote for the prototype the whole Park Crew carries forward."
      : " Ready up when you want to begin a fresh campaign.");
}

let raidWarningTimer: number | null = null;
let raidWarningSequence = 0;
let activeRaidWarningKind: RaidWarningMessage["kind"] | null = null;
let queuedRaidWarning: RaidWarningMessage | null = null;

function showRaidWarning(message: RaidWarningMessage): void {
  if (
    activeRaidWarningKind &&
    !dom.raidWarning.classList.contains("is-hidden") &&
    raidWarningPriority(activeRaidWarningKind) > raidWarningPriority(message.kind)
  ) {
    queuedRaidWarning = message;
    return;
  }
  raidWarningSequence += 1;
  const sequence = raidWarningSequence;
  if (raidWarningTimer) window.clearTimeout(raidWarningTimer);
  activeRaidWarningKind = message.kind;
  dom.raidWarning.dataset.kind = message.kind;
  dom.raidWarningKicker.textContent =
    message.kind === "combo"
      ? "COORDINATION CHECK"
      : message.kind === "frenzy"
        ? "TEAM POWER-UP"
        : message.kind === "overdrive"
          ? "POWER DROP"
          : message.kind === "boss"
            ? "FINAL BOSS"
            : message.kind === "bossphase"
              ? "BOSS PHASE CHANGE"
              : message.kind === "ultimate"
                ? "PARK CREW VOLLEY"
            : message.kind === "cube"
              ? "LETHAL PARK THREAT"
              : message.kind === "imposter"
                ? "DECEPTION PROTOCOL"
                : message.kind === "laser"
                  ? "LIVE FENCE CABLE"
                  : message.kind === "fog"
                    ? "VISION LOST"
                      : message.kind === "pools"
                        ? "BOG HAZARD"
                        : message.kind === "army"
                          ? "MOVING DAMAGE WALL"
                      : message.kind === "powerup"
                        ? "RARE POWERUP"
            : message.kind === "miniboss"
              ? "MINIBOSS INCOMING"
              : message.kind === "surge"
                ? "CONTAINMENT TEAMWORK"
                : message.kind === "named"
                  ? "ESCAPED SPECIMEN"
                  : "PRIORITY TARGET";
  dom.raidWarningTitle.textContent = message.title;
  dom.raidWarningCopy.textContent = message.text;
  dom.raidWarning.classList.remove("is-hidden", "show");
  void dom.raidWarning.offsetWidth;
  dom.raidWarning.classList.add("show");
  const scene = game.scene.getScene("vault") as VaultScene;
  const major =
    message.kind === "boss" ||
    message.kind === "bossphase" ||
    message.kind === "ultimate" ||
    message.kind === "cube" ||
    message.kind === "army";
  scene.cameras.main.shake(major ? 650 : 320, major ? 0.012 : 0.006);
  raidWarningTimer = window.setTimeout(() => {
    if (sequence !== raidWarningSequence) return;
    dom.raidWarning.classList.remove("show");
    window.setTimeout(() => {
      if (sequence !== raidWarningSequence) return;
      dom.raidWarning.classList.add("is-hidden");
      activeRaidWarningKind = null;
      if (queuedRaidWarning) {
        const queued = queuedRaidWarning;
        queuedRaidWarning = null;
        showRaidWarning(queued);
      }
    }, 220);
    raidWarningTimer = null;
  }, message.kind === "army" ? 2_400 : major ? 4_500 : 3_300);
}

function raidWarningPriority(kind: RaidWarningMessage["kind"]): number {
  if (
    kind === "boss" ||
    kind === "bossphase" ||
    kind === "ultimate" ||
    kind === "cube" ||
    kind === "army"
  ) {
    return 3;
  }
  if (
    kind === "guardian" ||
    kind === "overdrive" ||
    kind === "frenzy" ||
    kind === "surge"
  ) {
    return 2;
  }
  return 1;
}

function showToast(text: string): void {
  const toast = document.createElement("div");
  toast.className = "toast";
  toast.textContent = text;
  dom.toastStack.appendChild(toast);
  while (dom.toastStack.childElementCount > 4) {
    dom.toastStack.firstElementChild?.remove();
  }
  window.setTimeout(() => toast.classList.add("show"), 20);
  window.setTimeout(() => {
    toast.classList.remove("show");
    window.setTimeout(() => toast.remove(), 300);
  }, 2_700);
}

let damageFlashTimer: number | null = null;

function flashDamage(): void {
  document.body.classList.remove("damage-flash");
  void document.body.offsetWidth;
  document.body.classList.add("damage-flash");
  if (damageFlashTimer) window.clearTimeout(damageFlashTimer);
  damageFlashTimer = window.setTimeout(() => {
    document.body.classList.remove("damage-flash");
    damageFlashTimer = null;
  }, 430);
}

function flashRaidUltimate(): void {
  document.body.classList.remove("raid-ultimate-flash");
  void document.body.offsetWidth;
  document.body.classList.add("raid-ultimate-flash");
  window.setTimeout(
    () => document.body.classList.remove("raid-ultimate-flash"),
    1_250,
  );
}

function stageName(stage: MatchStage): string {
  return {
    descent: "THE TOUR",
    escalation: "THE PARK STIRS",
    chaos: "GREED UNBOUND",
    boss: "THE T-REX",
    results: "RUN COMPLETE",
  }[stage];
}

function formatTime(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes + ":" + String(seconds).padStart(2, "0");
}

function abilityGlyph(ability: AbilityId): string {
  return {
    ember: "✦",
    moon: "☾",
    chain: "ϟ",
    void: "◆",
    frost: "❄",
    solar: "☀",
    raven: "♠",
    necro: "◉",
    bard: "♪",
  }[ability];
}

// Which creature sheet renders each enemy kind. Wire ids are unchanged — this
// is purely the visual mapping; weak points remain equipment instead of dinos.
function dinoForKind(
  kind: EnemyTuple[1],
): { key: string; left: string; right: string } | null {
  switch (kind) {
    case "gnawer":
      return { key: "compy", left: "compy-left", right: "compy-right" };
    case "charger":
      return { key: "galli", left: "galli-left", right: "galli-right" };
    case "shardmother":
      return { key: "enemy-nest-matriarch", left: "", right: "" };
    case "stalker":
      return { key: "raptor", left: "raptor-left", right: "raptor-right" };
    case "spitter":
    case "hexweaver":
    case "veilkeeper":
      return { key: "dilo", left: "dilo-left", right: "dilo-right" };
    case "mirecaller":
      return { key: "enemy-bog-spitter", left: "", right: "" };
    case "brute":
    case "guardian":
    case "miniboss":
      return { key: "carno", left: "carno-left", right: "carno-right" };
    case "gravemaw":
      return { key: "enemy-bog-carno", left: "", right: "" };
    case "bulwark":
      return { key: "brachio", left: "brachio-left", right: "brachio-right" };
    case "skitter":
      return { key: "compy", left: "compy-left", right: "compy-right" };
    case "nullbell":
      return { key: "enemy-siren-ptera", left: "", right: "" };
    case "arcspinner":
      return { key: "enemy-fence-jumper", left: "", right: "" };
    case "imposter":
      return { key: "jham", left: "jham-idle", right: "jham-idle" };
    case "gelatinous":
      return { key: "ankylo", left: "ankylo-left", right: "ankylo-right" };
    case "boss":
      return { key: "trex", left: "trex-left", right: "trex-right" };
    default:
      return null;
  }
}

// Art resolution and gameplay collision size are intentionally independent.
// Each species gets a readable world-space silhouette while server hitboxes
// continue to use the unchanged enemy `size` value.
function enemySpriteWorldWidth(
  kind: EnemyTuple[1],
  key: string,
  size: number,
  nativeWidth: number,
): number {
  switch (key) {
    case "jham":
      return 64;
    case "compy":
      return Math.max(46, size * 2.9);
    case "galli":
      return Math.max(56, size * 3.1);
    case "dilo":
      return Math.max(52, size * 2.7);
    case "raptor":
      return Math.max(64, size * 3);
    case "carno":
      return Math.max(84, size * 3);
    case "brachio":
      return Math.max(126, size * 2.95);
    case "spino":
      return Math.max(144, size * 2.8);
    case "ankylo":
      return Math.min(192, Math.max(132, size * 1.8));
    case "trex":
      return Math.max(240, size * 2.4);
    default:
      return isIllustratedNamedEnemy(kind)
        ? Math.max(nativeWidth * 1.25, size * 2.8)
        : Math.max(nativeWidth, size * 2.8);
  }
}

function isIllustratedNamedEnemy(kind: EnemyTuple[1]): boolean {
  return (
    kind === "arcspinner" ||
    kind === "mirecaller" ||
    kind === "gravemaw" ||
    kind === "shardmother" ||
    kind === "nullbell"
  );
}

function enemyColor(kind: EnemyTuple[1]): number {
  return {
    gnawer: 0xff5678,
    charger: 0xff934f,
    spitter: 0x77dc8b,
    brute: 0xa563e8,
    skitter: 0x6ef7ff,
    bulwark: 0xd79b3f,
    stalker: 0xff477d,
    hexweaver: 0xb74cff,
    imposter: 0xff244f,
    arcspinner: 0x2dd7e8,
    veilkeeper: 0x504a72,
    mirecaller: 0x9d45bd,
    gravemaw: 0xc45535,
    nullbell: 0x756e9f,
    shardmother: 0x257f9e,
    gelatinous: 0x58d85d,
    weakpoint: 0xffd34f,
    miniboss: 0xc55cff,
    guardian: 0x27bda0,
    boss: 0x8d245e,
  }[kind];
}

function isNamedEnemy(
  kind: EnemyTuple[1],
): kind is
  | "bulwark"
  | "stalker"
  | "hexweaver"
  | "imposter"
  | "arcspinner"
  | "veilkeeper"
  | "mirecaller"
  | "gravemaw"
  | "nullbell"
  | "shardmother" {
  return (
    kind === "bulwark" ||
    kind === "stalker" ||
    kind === "hexweaver" ||
    kind === "imposter" ||
    kind === "arcspinner" ||
    kind === "veilkeeper" ||
    kind === "mirecaller" ||
    kind === "gravemaw" ||
    kind === "nullbell" ||
    kind === "shardmother"
  );
}

function namedEnemyName(
  kind:
    | "bulwark"
    | "stalker"
    | "hexweaver"
    | "imposter"
    | "arcspinner"
    | "veilkeeper"
    | "mirecaller"
    | "gravemaw"
    | "nullbell"
    | "shardmother",
): string {
  return {
    bulwark: "PANICKED BRACHIO",
    stalker: "RAPTOR STALKER",
    hexweaver: "DILO MATRIARCH",
    imposter: "COMPANY MOLE",
    arcspinner: "FENCE JUMPER",
    veilkeeper: "VENOM SPRAYER",
    mirecaller: "BOG SPITTER",
    gravemaw: "BOG CARNO",
    nullbell: "SIREN PTERA",
    shardmother: "NEST MATRIARCH",
  }[kind];
}

function powerUpColor(kind: PowerUpTuple[1]): number {
  return {
    fury: 0xff515b,
    haste: 0x5feaff,
    shield: 0x82a7ff,
    nova: 0xffd65a,
    jello: 0x9de85f,
    egg: 0xffefb0,
    bait: 0xff765c,
    truesight: 0xe78cff,
    barrage: 0xff62df,
    rod: 0x63ead7,
    sea_bass: 0xffc85a,
    energy_bar: 0xf9c80e,
    ice_pop: 0x9fe8ff,
    canteen: 0x8fcf78,
  }[kind];
}

const POWERUP_ART: Partial<Record<PowerUpTuple[1], string>> = {
  fury: "pu-fury",
  haste: "pu-haste",
  shield: "pu-shield",
  nova: "pu-nova",
  jello: "pu-jello",
  egg: "pu-egg",
  bait: "pu-bait",
  truesight: "pu-truesight",
  barrage: "pu-barrage",
  rod: "pu-rod",
  sea_bass: "pu-sea-bass",
  energy_bar: "pu-energy-bar",
  ice_pop: "pu-ice-pop",
  canteen: "pu-canteen",
};

function powerUpGlyph(kind: PowerUpTuple[1]): string {
  return {
    fury: "⚔",
    haste: "»",
    shield: "⬡",
    nova: "✹",
    jello: "≈",
    egg: "●",
    bait: "◆",
    truesight: "◉",
    barrage: "Ⅵ",
    rod: "R",
    sea_bass: "F",
    energy_bar: "E",
    ice_pop: "I",
    canteen: "W",
  }[kind];
}

function destroyPlayerView(view: PlayerView): void {
  view.body.destroy();
  view.sprite.destroy();
  view.outline.destroy();
  view.name.destroy();
  view.evolution.destroy();
  view.level.destroy();
  view.rune.destroy();
  view.revive.destroy();
}

function destroyEnemyView(view: EnemyView): void {
  view.body.destroy();
  view.sprite?.destroy();
  view.healthBg.destroy();
  view.health.destroy();
  view.label?.destroy();
  view.rune?.destroy();
}

function destroySummonView(view: SummonView): void {
  view.body.destroy();
  view.sprite.destroy();
  view.outline.destroy();
  view.rune.destroy();
}

function percentage(value: number): string {
  return Math.round(Phaser.Math.Clamp(value, 0, 1) * 100) + "%";
}

function randomName(): string {
  const first = ["Rookie", "Lucky", "Night", "Field", "Trail", "Brave"];
  const second = ["Ranger", "Handler", "Guide", "Tracker", "Tech", "Driver"];
  return (
    first[Math.floor(Math.random() * first.length)] +
    " " +
    second[Math.floor(Math.random() * second.length)]
  );
}

function escapeHtml(value: string): string {
  const element = document.createElement("span");
  element.textContent = value;
  return element.innerHTML;
}

function required<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error("Missing #" + id);
  return element as T;
}

setupTouchControls();
void game;
